import { supabase } from '../lib/supabase';
import { getNexusHostingMonthlyBillableUsd } from '../lib/nexusHostingSubscription';
import {
  convertCentsToCadCents,
  convertDollarsToCad,
  type ExchangeRatesToCad,
  getExchangeRatesToCad,
} from '../lib/currencyToCad';

interface SignupFunnelEventRow {
  session_id: string;
  visitor_id: string | null;
  flow: string;
  event_type: string;
  step: string | null;
  event_data: unknown;
  referral_code: string | null;
  utm: unknown;
  referrer: string | null;
  page_url: string | null;
  created_at: string;
}

/** True when the event came from the Signup-nexus landing route. */
function isSignupNexusPageUrl(pageUrl?: string | null): boolean {
  if (!pageUrl) return false;
  try {
    const path = new URL(pageUrl).pathname.toLowerCase();
    return path === '/signup-nexus' || path.startsWith('/signup-nexus/');
  } catch {
    return /\/signup-nexus\b/i.test(pageUrl);
  }
}

export interface SignupFunnelAnalytics {
  totals: { sessions: number; visitors: number; completed: number; conversionRate: number };
  funnel: { step: string; label: string; sessions: number; pctOfTop: number; dropFromPrev: number }[];
  daily: { date: string; sessions: number; completed: number }[];
  sources: { source: string; sessions: number; completed: number }[];
  errors: { reason: string; count: number }[];
  flows: { flow: string; sessions: number; completed: number }[];
}

/** One counting mode (all sessions vs. unique visitors) of a flow funnel. */
export interface SignupFlowConversionMode {
  /** e.g. "12.5%" — completed / step-1 starts */
  value: string;
  /** e.g. "Conversion Rate (5/40)" */
  label: string;
  funnelData: { step: string; count: number }[];
}

/** One KPI-style conversion funnel for a single signup entry path. */
export interface SignupFlowConversionReport {
  key: 'standard' | 'extended' | 'partner' | 'nexus';
  title: string;
  /** Public entry path for this flow (shown on the Super Admin card). */
  url: string;
  /** Counting every session. */
  all: SignupFlowConversionMode;
  /** Counting distinct visitors (returning visitors collapse into one). */
  unique: SignupFlowConversionMode;
}

/** Email captured at the signup email step (lead list for Sign up flows). */
export interface SignupCollectedEmail {
  email: string;
  flow: string;
  /** Where it was captured (hero / footer / email page), when known. */
  captureSource: string | null;
  collectedAt: string;
  sessionId: string;
  completedSignup: boolean;
  referralCode: string | null;
}

export interface SocialSeatUsage {
  active_count: number;
  purchased_quantity: number;
  grandfathered_quantity: number;
  total_capacity: number;
  can_add: boolean;
}

export interface TrialAnalytics {
  totals: {
    /** Businesses currently in a Stripe trial. */
    activeTrials: number;
    /** Of the active trials, how many already added a card. */
    activeWithCard: number;
    /** Trials that ended and turned into paying subscriptions. */
    converted: number;
    /** Trials that ended without ever paying (canceled/expired). */
    lost: number;
    /** converted / (converted + lost) as a percent. */
    conversionRate: number;
    /** All platform subscriptions that ever had a trial. */
    totalTrials: number;
  };
  /** Businesses currently trialing, soonest-ending first. */
  activeTrialList: {
    businessId: string | null;
    businessName: string | null;
    email: string | null;
    planLabel: string | null;
    amountMonthly: number | null;
    trialStart: string | null;
    trialEnd: string | null;
    daysLeft: number | null;
    hasCard: boolean;
  }[];
  /** True when no mirrored subscription has trial data yet (sync not run since upgrade). */
  needsSync: boolean;
}

export type EngagementBucket = 'daily' | 'every3' | 'weekly' | 'monthly' | 'inactive';

export interface EngagementGroup {
  /** Bucket → businesses in it (each business lands in exactly one bucket). */
  buckets: Record<EngagementBucket, { businessId: string; name: string | null; activeDays: number }[]>;
  total: number;
}

export interface EngagementAnalytics {
  windowDays: number;
  trial: EngagementGroup;
  paid: EngagementGroup;
}

/** One visitor journey through signup — a card in the rolling signup log. */
export interface SignupSessionCard {
  sessionId: string;
  flow: string;
  startedAt: string;
  lastSeenAt: string;
  source: string;
  referralCode: string | null;
  /** Email once the visitor typed it (also backfilled from the created business). */
  email: string | null;
  businessId: string | null;
  businessName: string | null;
  status: 'completed' | 'active' | 'dropped';
  /** Ordered journey stages for this flow, with how far the visitor got. */
  stages: string[];
  stageIndex: number;
  stageLabel: string;
  errors: string[];
  planSelected: string | null;
}

export interface RemoteSession {
  id: string;
  admin_id: string;
  target_user_id: string;
  business_id: string;
  status: 'pending' | 'approved' | 'active' | 'paused' | 'ended' | 'rejected' | 'expired';
  session_code: string;
  created_at: string;
  approved_at: string | null;
  ended_at: string | null;
  ended_by: 'admin' | 'user' | 'timeout' | 'app_closed' | null;
}

export interface BusinessUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  auth_user_id: string | null;
  status: string;
}

export interface PlatformMetrics {
  totalBusinesses: number;
  activeBusinesses: number;
  totalUsers: number;
  activeUsers: number;
  totalBookings: number;
  monthlyBookings: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
}

export interface BusinessSummary {
  id: string;
  name: string;
  owner_id: string;
  owner_email: string;
  owner_name: string;
  type: string;
  plan_name: string;
  addon_apps: string[];
  /** Platform plan + active add-ons (excludes website hosting). */
  nexus_monthly: number;
  /** Website hosting when a billed Stripe hosting subscription exists. */
  hosting_monthly: number;
  /** nexus_monthly + hosting_monthly */
  monthly_cost: number;
  created_at: string;
  user_count: number;
  booking_count: number;
  sales_amount: number;
  last_activity: string;
  /** Derived from businesses.subscription_status (+ plan) for Super Admin list, filters, and badge. */
  status: SuperAdminBusinessListStatus;
  frozen: boolean;
  frozen_at?: string;
  frozen_by?: string;
  frozen_reason?: string;
  integrations: {
    twilio: boolean;
    gmail: boolean;
    stripe: boolean;
  };
  logo_url?: string;
  phone?: string;
  /** Nexus-built hosted site URL (super admin) */
  nexus_website_url?: string | null;
  nexus_hosting_stripe_subscription_id?: string | null;
  nexus_hosting_subscription_status?: string | null;
  /** Vercel project backing the Nexus site (enables pause/unpause enforcement). */
  vercel_project_id?: string | null;
  /** Last state pushed to Vercel (true = paused). Null = never synced. */
  vercel_hosting_paused?: boolean | null;
  /** Active Stripe subscriptions from mirror (platform account billing). */
  stripe_subscriptions?: BusinessStripeSubscriptionSummary[];
  active_plan_id?: string | null;
  subscription_status_raw?: string | null;
  platform_plan_price?: number;
  /** Set when access comes from Grant Access (no Stripe platform subscription). */
  admin_access?: {
    kind: 'trial' | 'complimentary';
    trial_end_date?: string | null;
  } | null;
  /** True for Stripe `trialing` subscriptions AND admin-granted trials (Businesses tab "Trialling" filter). */
  is_trialing?: boolean;
}

export interface BusinessStripeSubscriptionSummary {
  stripe_subscription_id: string;
  kind: string;
  status: string;
  plan_label: string | null;
  amount_monthly: number | null;
  currency: string;
  interval: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  next_payment_attempt: string | null;
}

/** Super Admin businesses tab + badge; past_due still appears under the "Active" filter. */
export type SuperAdminBusinessListStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'inactive'
  | 'suspended';

export function mapSubscriptionToSuperAdminBusinessStatus(
  subscriptionStatus: string | null | undefined,
  activePlanId: string | null | undefined,
): SuperAdminBusinessListStatus {
  const raw = (subscriptionStatus ?? '').trim().toLowerCase();
  if (raw === 'past_due') return 'past_due';
  if (raw === 'canceled' || raw === 'cancelled') return 'canceled';
  if (raw === 'inactive') return 'inactive';
  if (raw === 'active' || raw === 'trialing') return 'active';
  if (activePlanId) return 'active';
  return 'inactive';
}

export function isCanceledStripeSubscriptionStatus(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'canceled' || s === 'cancelled' || s === 'incomplete_expired' || s === 'incomplete';
}

export interface UserSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  business_name: string;
  business_id: string;
  created_at: string;
  last_sign_in_at: string;
  is_business_owner: boolean;
}

export interface SuperAdminRequest {
  id: string;
  business_id: string;
  user_id: string | null;
  source: string;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  business_name?: string;
}

export interface StripeInvoiceMirrorRow {
  id: string;
  stripe_invoice_id: string;
  business_id: string | null;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  number: string | null;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  invoice_created_at: string;
  due_date: string | null;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  customer_email: string | null;
  description: string | null;
  businesses?: { name: string } | null;
}

export type StripeInvoiceAdminAction =
  | 'mark_paid'
  | 'void'
  | 'mark_uncollectible'
  | 'finalize'
  | 'send'
  | 'delete_draft';

export interface StripeInvoiceActionResult {
  ok: boolean;
  action?: StripeInvoiceAdminAction;
  deleted?: boolean;
  stripeInvoiceId?: string;
  invoice?: {
    stripe_invoice_id: string;
    status: string;
    amount_due: number;
    amount_paid: number;
    paid_at: string | null;
  };
  error?: string;
}

export interface StripeSubscriptionMirrorRow {
  id: string;
  stripe_subscription_id: string;
  business_id: string | null;
  stripe_customer_id: string;
  kind: string;
  status: string;
  plan_label: string | null;
  amount_monthly: number | null;
  currency: string;
  interval: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  next_payment_attempt: string | null;
}

export interface StripeBillingSyncResult {
  ok: boolean;
  invoicesSynced: number;
  subscriptionsSynced: number;
  message?: string;
  error?: string;
}

export interface StripeBillingMetrics {
  /**
   * Headline MRR in CAD dollars — prefers Stripe Analytics `revenue.mrr`
   * (same definition as the Stripe Dashboard). Falls back to mirror sum if Stripe fails.
   */
  mrr_total: number;
  /** True when mrr_total came from Stripe Analytics API. */
  mrr_from_stripe: boolean;
  mrr_platform: number;
  mrr_hosting: number;
  /** Add-on apps, team seats, etc. on Nexus businesses. */
  mrr_other: number;
  /** Stripe subscriptions with no linked Nexus business (external customers). */
  mrr_external: number;
  external_subscriptions: number;
  active_subscriptions: number;
  trialing_subscriptions: number;
  past_due_subscriptions: number;
  /** Paid invoice revenue this calendar month (cents). */
  collected_this_month: number;
  /** All-time paid invoice revenue (cents). */
  collected_all_time: number;
  /** Open invoice balance due (cents). */
  outstanding: number;
  open_invoices: number;
  paid_invoices: number;
  total_invoices: number;
  /**
   * Revenue broken down by Stripe product id for active + past_due only
   * (trials excluded, matching Stripe MRR), sorted by monthly total (CAD) descending.
   */
  by_item: StripeItemBreakdownRow[];
  /** AI token purchase revenue this calendar month (CAD cents). */
  ai_tokens_this_month: number;
  /** All-time AI token purchase revenue (CAD cents). */
  ai_tokens_all_time: number;
  /** Number of paid AI token purchases this month. */
  ai_token_purchases_this_month: number;
  /** Tokens credited via paid purchases this month. */
  ai_tokens_units_this_month: number;
  /** All monetary fields above are normalized to CAD. */
  display_currency: 'CAD';
}

/** One recurring subscription placed on the day it bills. Amount is the monthly equivalent in `currency` dollars. */
export interface RecurringBillAnchor {
  amountMonthly: number;
  currency: string;
  /** Stripe interval: month, year, week, or day. Null is treated as month. */
  interval: string | null;
  /** A timestamp that falls on the renewal day (Vancouver). */
  anchorIso: string;
  /** Charges before this date are omitted. Set for trials, whose first bill is the trial end. */
  notBeforeIso: string | null;
  cancelAtIso: string | null;
}

export interface AiTokenPurchaseRow {
  id: string;
  business_id: string;
  business_name: string | null;
  tokens: number;
  amount_cents_usd: number;
  amount_cad_cents: number;
  operation_type: string | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
}

/** Extract USD cents from ai_token_transactions.metadata (manual vs auto-topup). */
function aiTokenPurchaseAmountCentsUsd(metadata: unknown): number {
  if (!metadata || typeof metadata !== 'object') return 0;
  const m = metadata as Record<string, unknown>;
  if (typeof m.amount_cents === 'number' && Number.isFinite(m.amount_cents)) {
    return Math.round(m.amount_cents);
  }
  if (typeof m.amount_cents === 'string' && m.amount_cents.trim()) {
    const n = Number(m.amount_cents);
    if (Number.isFinite(n)) return Math.round(n);
  }
  // Manual purchase stores dollars in amount_paid
  if (typeof m.amount_paid === 'number' && Number.isFinite(m.amount_paid)) {
    return Math.round(m.amount_paid * 100);
  }
  if (typeof m.amount_paid === 'string' && m.amount_paid.trim()) {
    const n = Number(m.amount_paid);
    if (Number.isFinite(n)) return Math.round(n * 100);
  }
  return 0;
}

export interface StripeItemBreakdownRow {
  /** Display name from Stripe product (or unlabeled fallback). */
  label: string;
  /** Stripe product id used for grouping; null when unavailable. */
  product_id: string | null;
  /** Number of subscription line items in this product bucket. */
  count: number;
  /** Combined normalized monthly amount in CAD (dollars), quantity included. */
  monthly_total: number;
}

export interface ReferralPartnerRow {
  id: string;
  name: string;
  code: string;
  email: string | null;
  status: 'active' | 'inactive' | 'awaiting_approval';
  commission_type: 'one_time' | 'recurring';
  commission_percent: number;
  signup_discount_enabled: boolean;
  signup_discount_percent: number | null;
  stripe_coupon_id: string | null;
  notes: string | null;
  invite_token: string;
  claimed_at: string | null;
  agreement_signed_at: string | null;
  agreement_signer_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReferralPartnerListItem extends ReferralPartnerRow {
  signups: number;
  accrued_cents: number;
  paid_cents: number;
}

export interface ReferralAttributionRow {
  id: string;
  business_id: string;
  partner_id: string;
  link_code: string;
  commission_type: string;
  commission_percent: number;
  status: string;
  stripe_subscription_id: string | null;
  attributed_at: string;
  activated_at: string | null;
  first_commission_at: string | null;
  one_time_commission_paid: boolean;
  businesses?: { name: string; email: string | null } | null;
}

export interface ReferralCommissionRow {
  id: string;
  partner_id: string;
  business_id: string;
  stripe_invoice_id: string;
  invoice_paid_at: string | null;
  eligible_gross_cents: number;
  commission_percent: number;
  commission_amount_cents: number;
  currency: string;
  commission_kind: string;
  status: string;
  created_at: string;
  businesses?: { name: string } | null;
}

export interface CreateReferralPartnerParams {
  name: string;
  code: string;
  email?: string;
  commission_type: 'one_time' | 'recurring';
  commission_percent: number;
  signup_discount_enabled?: boolean;
  signup_discount_percent?: number | null;
  notes?: string;
}

export interface CreateReferralPartnerResult {
  partner: ReferralPartnerRow;
  couponSyncWarning?: string;
}

function formatReferralPartnerDbError(error: {
  code?: string;
  message?: string;
  details?: string;
}): string {
  if (error.code === '23505') {
    return 'A partner with this code already exists. Pick a different code, or expand the list below — a previous attempt may have created the partner without finishing setup.';
  }
  if (error.code === '23514') {
    return 'Database migration required: run 20260530110000_referral_partner_invite_claim.sql in Supabase (awaiting_approval status).';
  }
  if (
    error.code === 'PGRST204' ||
    error.message?.includes('signup_discount') ||
    error.message?.includes('invite_token')
  ) {
    return 'Database migrations required: apply referral partner migrations in Supabase (invite claim + signup discount).';
  }
  return error.message || 'Failed to create partner';
}

/** Normalize Stripe recurring price to monthly cents. */
export function normalizeStripeRecurringToMonthlyCents(
  cents: number,
  recurring?: { interval?: string; interval_count?: number } | null,
): number {
  const interval = recurring?.interval || 'month';
  const intervalCount = Math.max(1, recurring?.interval_count ?? 1);
  switch (interval) {
    case 'year':
      return cents / (12 * intervalCount);
    case 'month':
      return cents / intervalCount;
    case 'week':
      return (cents * 52) / (12 * intervalCount);
    case 'day':
      return (cents * 365) / (12 * intervalCount);
    default:
      return cents / intervalCount;
  }
}

/** Compute normalized monthly amount (in subscription currency) from mirrored subscription items JSON. */
export function monthlyAmountFromMirrorItems(items: unknown): number {
  const data =
    items && typeof items === 'object' && 'data' in items
      ? (items as { data?: unknown[] }).data
      : null;
  if (!Array.isArray(data)) return 0;

  let totalCents = 0;
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const price = row.price as Record<string, unknown> | undefined;
    const unitAmount = price?.unit_amount;
    const qty = typeof row.quantity === 'number' ? row.quantity : 1;
    if (typeof unitAmount !== 'number') continue;
    const recurring = price?.recurring as { interval?: string; interval_count?: number } | undefined;
    totalCents += normalizeStripeRecurringToMonthlyCents(unitAmount * qty, recurring);
  }
  return totalCents > 0 ? totalCents / 100 : 0;
}

export function resolveMirrorSubscriptionMonthlyAmount(
  amountMonthly: number | null | undefined,
  items: unknown,
): number {
  const fromItems = monthlyAmountFromMirrorItems(items);
  if (fromItems > 0) return fromItems;
  if (amountMonthly != null && Number.isFinite(Number(amountMonthly))) {
    return Number(amountMonthly);
  }
  return 0;
}

interface MirrorItemEntry {
  priceId: string | null;
  /** Stripe product id (from expanded product object or bare product id string). */
  productId: string | null;
  /** Stripe product name (present when the sync enriched price.product). */
  productName: string | null;
  nickname: string | null;
  lookupKey: string | null;
  /** Normalized monthly amount in the subscription's currency (dollars), quantity included. */
  monthly: number;
}

/** Extract per-line-item entries (price identity + normalized monthly amount) from mirrored items JSON. */
function itemEntriesFromMirrorItems(items: unknown): MirrorItemEntry[] {
  const data =
    items && typeof items === 'object' && 'data' in items
      ? (items as { data?: unknown[] }).data
      : null;
  if (!Array.isArray(data)) return [];

  const entries: MirrorItemEntry[] = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const price = row.price as Record<string, unknown> | undefined;
    const unitAmount = price?.unit_amount;
    if (typeof unitAmount !== 'number') continue;
    const qty = typeof row.quantity === 'number' ? row.quantity : 1;
    const recurring = price?.recurring as { interval?: string; interval_count?: number } | undefined;
    const product = price?.product as { id?: string; name?: string } | string | undefined;
    let productId: string | null = null;
    let productName: string | null = null;
    if (typeof product === 'string' && product.trim()) {
      productId = product.trim();
    } else if (product && typeof product === 'object') {
      if (typeof product.id === 'string' && product.id.trim()) productId = product.id.trim();
      if (typeof product.name === 'string' && product.name.trim()) productName = product.name.trim();
    }
    entries.push({
      priceId: typeof price?.id === 'string' ? price.id : null,
      productId,
      productName,
      nickname:
        typeof price?.nickname === 'string' && price.nickname.trim() ? price.nickname.trim() : null,
      lookupKey:
        typeof price?.lookup_key === 'string' && price.lookup_key.trim()
          ? price.lookup_key.trim()
          : null,
      monthly: normalizeStripeRecurringToMonthlyCents(unitAmount * qty, recurring) / 100,
    });
  }
  return entries;
}

export interface SubscriptionPlanOption {
  plan_id: string;
  plan_name: string;
  plan_price: number | null;
  included_app_count: number;
}

export type GrantBusinessAccessMode = 'trial' | 'complimentary';

export interface GrantBusinessAccessParams {
  businessId: string;
  planId?: string;
  accessMode: GrantBusinessAccessMode;
  trialEndDate?: string;
  installAllApps?: boolean;
  notes?: string;
}

export interface GrantBusinessAccessResult {
  success: boolean;
  message: string;
  planId?: string;
  planName?: string;
  appsInstalled?: number;
  accessMode?: GrantBusinessAccessMode;
  error?: string;
}

export class SuperAdminService {
  /**
   * Get platform-wide metrics overview
   */
  static async getPlatformMetrics(): Promise<PlatformMetrics | null> {
    if (!supabase) return null;

    try {
      // Get total businesses
      const { count: totalBusinesses } = await supabase
        .from('businesses')
        .select('*', { count: 'exact', head: true });

      // Get active businesses (those with activity in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: activeBusinesses } = await supabase
        .from('businesses')
        .select('*', { count: 'exact', head: true })
        .gte('updated_at', thirtyDaysAgo.toISOString());

      // Get total users from auth.users
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // Get active users (signed in within last 30 days)
      const { count: activeUsers } = await supabase
        .rpc('count_active_users', { days_threshold: 30 });

      // Get total bookings
      const { count: totalBookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', false);

      // Get monthly bookings
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const { count: monthlyBookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', false)
        .gte('created_at', monthStart.toISOString());

      // Simple system health check (can be enhanced later)
      const systemHealth: 'healthy' | 'warning' | 'critical' = 
        (activeBusinesses || 0) > 0 && (activeUsers || 0) > 0 ? 'healthy' : 'warning';

      return {
        totalBusinesses: totalBusinesses || 0,
        activeBusinesses: activeBusinesses || 0,
        totalUsers: totalUsers || 0,
        activeUsers: activeUsers || 0,
        totalBookings: totalBookings || 0,
        monthlyBookings: monthlyBookings || 0,
        systemHealth
      };
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      return null;
    }
  }

  /**
   * Get list of all businesses with summary data
   */
  static async getAllBusinesses(): Promise<BusinessSummary[]> {
    if (!supabase) return [];

    try {
      // Single query: fetch business data + subscription fields in one go
      const { data: businesses, error } = await supabase
        .from('businesses')
        .select('id, name, type, phone, created_at, updated_at, owner_id, frozen, frozen_at, frozen_by, frozen_reason, subscription_status, active_plan_id, stripe_customer_id, stripe_subscription_id, trial_enabled, trial_end_date, twilio_enabled, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, stripe_connect_account_id, stripe_connect_onboarding_completed, booking_settings, nexus_website_url, nexus_hosting_stripe_subscription_id, nexus_hosting_subscription_status, vercel_project_id, vercel_hosting_paused')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!businesses || businesses.length === 0) return [];

      const businessIds = businesses.map(b => b.id);
      const ownerIds = businesses.map(b => b.owner_id).filter(Boolean);
      const customerIds = [
        ...new Set(
          businesses
            .map((b) => (b as { stripe_customer_id?: string | null }).stripe_customer_id?.trim())
            .filter((id): id is string => !!id),
        ),
      ];
      const customerIdByBusiness = new Map<string, string>();
      for (const b of businesses) {
        const custId = (b as { stripe_customer_id?: string | null }).stripe_customer_id?.trim();
        if (custId) customerIdByBusiness.set(b.id, custId);
      }

      // Batch queries: run all lookups in parallel instead of per-business
      const [
        { data: allOwnersByBiz },
        { data: allOwnersByUserId },
        { data: allGmailConnections },
        { data: allSmtpConfigs },
        { data: allAddonSubs },
        { data: allPlans },
        { data: stripeSubsByBusiness },
        { data: stripeSubsByCustomer },
      ] = await Promise.all([
        supabase.from('users').select('business_id, email, first_name, last_name').in('business_id', businessIds),
        ownerIds.length > 0
          ? supabase.from('users').select('id, email, first_name, last_name').in('id', ownerIds)
          : Promise.resolve({ data: [] as any[] }),
        supabase.from('gmail_connections').select('business_id, is_active').eq('is_active', true).in('business_id', businessIds),
        supabase.from('smtp_config').select('business_id, use_oauth, oauth_access_token').in('business_id', businessIds),
        supabase.from('business_addon_subscriptions').select('business_id, addon_apps ( name, price_monthly )').eq('status', 'active').in('business_id', businessIds),
        supabase.from('subscription_plans').select('plan_id, plan_name, plan_price'),
        supabase
          .from('stripe_subscriptions')
          .select('stripe_subscription_id, business_id, stripe_customer_id, kind, status, plan_label, amount_monthly, currency, interval, current_period_end, cancel_at, next_payment_attempt')
          .in('business_id', businessIds),
        customerIds.length > 0
          ? supabase
              .from('stripe_subscriptions')
              .select('stripe_subscription_id, business_id, stripe_customer_id, kind, status, plan_label, amount_monthly, currency, interval, current_period_end, cancel_at, next_payment_attempt')
              .in('stripe_customer_id', customerIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);

      // Index owners by user ID for direct owner_id lookup
      const ownerByUserId = new Map<string, { email: string; name: string }>();
      for (const o of allOwnersByUserId || []) {
        ownerByUserId.set(o.id, {
          email: o.email || '',
          name: `${o.first_name || ''} ${o.last_name || ''}`.trim(),
        });
      }

      // Index owners by business_id as fallback
      const ownerByBiz = new Map<string, { email: string; name: string }>();
      for (const o of allOwnersByBiz || []) {
        if (!ownerByBiz.has(o.business_id)) {
          ownerByBiz.set(o.business_id, {
            email: o.email || '',
            name: `${o.first_name || ''} ${o.last_name || ''}`.trim(),
          });
        }
      }

      const gmailByBiz = new Set((allGmailConnections || []).map(g => g.business_id));

      const smtpByBiz = new Map<string, boolean>();
      for (const s of allSmtpConfigs || []) {
        if (s.use_oauth && s.oauth_access_token) smtpByBiz.set(s.business_id, true);
      }

      const addonsByBiz = new Map<string, { names: string[]; cost: number }>();
      for (const sub of (allAddonSubs || []) as any[]) {
        const bizId = sub.business_id;
        const entry = addonsByBiz.get(bizId) || { names: [], cost: 0 };
        if (sub.addon_apps?.name) entry.names.push(sub.addon_apps.name);
        entry.cost += sub.addon_apps?.price_monthly || 0;
        addonsByBiz.set(bizId, entry);
      }

      const planPriceMap = new Map<string, number>();
      const planNameMap = new Map<string, string>();
      for (const p of allPlans || []) {
        planPriceMap.set(p.plan_id, p.plan_price || 0);
        planNameMap.set(p.plan_id, p.plan_name || p.plan_id);
      }

      const stripeSubsByBiz = new Map<string, Map<string, BusinessStripeSubscriptionSummary>>();
      const addStripeSubForBusiness = (businessId: string, sub: any) => {
        if (!businessId || !sub?.stripe_subscription_id) return;
        const bucket = stripeSubsByBiz.get(businessId) || new Map<string, BusinessStripeSubscriptionSummary>();
        bucket.set(sub.stripe_subscription_id, {
          stripe_subscription_id: sub.stripe_subscription_id,
          kind: sub.kind || 'other',
          status: sub.status || 'unknown',
          plan_label: sub.plan_label ?? null,
          amount_monthly: sub.amount_monthly != null ? Number(sub.amount_monthly) : null,
          currency: sub.currency || 'usd',
          interval: sub.interval ?? null,
          current_period_end: sub.current_period_end ?? null,
          cancel_at: sub.cancel_at ?? null,
          next_payment_attempt: sub.next_payment_attempt ?? null,
        });
        stripeSubsByBiz.set(businessId, bucket);
      };

      for (const sub of stripeSubsByBusiness || []) {
        if (sub.business_id) addStripeSubForBusiness(sub.business_id, sub);
      }

      for (const sub of stripeSubsByCustomer || []) {
        for (const [businessId, custId] of customerIdByBusiness.entries()) {
          if (sub.stripe_customer_id === custId) {
            addStripeSubForBusiness(businessId, sub);
          }
        }
      }

      const sortStripeSubs = (subs: BusinessStripeSubscriptionSummary[]) => {
        const activeStatuses = new Set(['active', 'trialing', 'past_due']);
        return [...subs]
          .filter((s) => !isCanceledStripeSubscriptionStatus(s.status))
          .sort((a, b) => {
          const aActive = activeStatuses.has(a.status) ? 0 : 1;
          const bActive = activeStatuses.has(b.status) ? 0 : 1;
          if (aActive !== bActive) return aActive - bActive;
          return (a.kind || '').localeCompare(b.kind || '');
        });
      };

      const getPlanName = (planId: string | null): string => {
        if (!planId) return 'No Plan';
        return planNameMap.get(planId) || `${planId.charAt(0).toUpperCase()}${planId.slice(1)} Plan`;
      };

      return businesses.map((business) => {
        const owner = (business.owner_id && ownerByUserId.get(business.owner_id)) || ownerByBiz.get(business.id);
        const activePlanId = business.active_plan_id;
        const subscriptionStatus = business.subscription_status;
        const addons = addonsByBiz.get(business.id) || { names: [], cost: 0 };
        const planPrice = activePlanId ? (planPriceMap.get(activePlanId) || 0) : 0;
        const nexusMonthly = planPrice + addons.cost;
        const hostingMonthly = getNexusHostingMonthlyBillableUsd(
          (business as { nexus_hosting_stripe_subscription_id?: string | null }).nexus_hosting_stripe_subscription_id,
          (business as { nexus_hosting_subscription_status?: string | null }).nexus_hosting_subscription_status,
          // DB-driven hosting price (subscription_plans 'nexus-hosting' row), when present.
          planPriceMap.get('nexus-hosting'),
        );

        const status = mapSubscriptionToSuperAdminBusinessStatus(subscriptionStatus, activePlanId);

        const stripeSubscriptions = sortStripeSubs(
          Array.from(stripeSubsByBiz.get(business.id)?.values() || []),
        );
        const hasStripePlatformSub = stripeSubscriptions.some(
          (s) => s.kind === 'platform' && !isCanceledStripeSubscriptionStatus(s.status),
        );

        const subStatusLower = (subscriptionStatus ?? '').trim().toLowerCase();
        const dbAccessActive =
          !!activePlanId && (subStatusLower === 'active' || subStatusLower === 'trialing');

        let admin_access: BusinessSummary['admin_access'] = null;
        if (dbAccessActive && !hasStripePlatformSub) {
          const trialEnabled =
            !!(business as { trial_enabled?: boolean }).trial_enabled &&
            !!(business as { trial_end_date?: string | null }).trial_end_date &&
            new Date((business as { trial_end_date: string }).trial_end_date) > new Date();
          if (trialEnabled) {
            admin_access = {
              kind: 'trial',
              trial_end_date: (business as { trial_end_date?: string | null }).trial_end_date ?? null,
            };
          } else {
            admin_access = { kind: 'complimentary' };
          }
        }

        const integrations = {
          gmail: gmailByBiz.has(business.id) || (smtpByBiz.get(business.id) ?? false),
          twilio: !!(business.twilio_enabled && business.twilio_account_sid && business.twilio_auth_token && business.VITE_TWILIO_PHONE_NUMBER),
          stripe: !!(business.stripe_connect_account_id && business.stripe_connect_onboarding_completed),
        };

        return {
          id: business.id,
          name: business.name || 'Unnamed Business',
          owner_id: business.owner_id || '',
          owner_email: owner?.email || '',
          owner_name: owner?.name || '',
          type: getPlanName(activePlanId),
          plan_name: getPlanName(activePlanId),
          addon_apps: addons.names,
          nexus_monthly: nexusMonthly,
          hosting_monthly: hostingMonthly,
          monthly_cost: nexusMonthly + hostingMonthly,
          created_at: business.created_at,
          user_count: 0,
          booking_count: 0,
          sales_amount: 0,
          last_activity: business.updated_at,
          status,
          frozen: business.frozen || false,
          frozen_at: business.frozen_at,
          frozen_by: business.frozen_by,
          frozen_reason: business.frozen_reason,
          integrations,
          logo_url: (business as any).booking_settings?.logo_url || undefined,
          phone: (business as any).phone || undefined,
          nexus_website_url: (business as { nexus_website_url?: string | null }).nexus_website_url ?? null,
          nexus_hosting_stripe_subscription_id:
            (business as { nexus_hosting_stripe_subscription_id?: string | null }).nexus_hosting_stripe_subscription_id ??
            null,
          nexus_hosting_subscription_status:
            (business as { nexus_hosting_subscription_status?: string | null }).nexus_hosting_subscription_status ??
            null,
          vercel_project_id: (business as { vercel_project_id?: string | null }).vercel_project_id ?? null,
          vercel_hosting_paused:
            (business as { vercel_hosting_paused?: boolean | null }).vercel_hosting_paused ?? null,
          stripe_subscriptions: stripeSubscriptions,
          active_plan_id: activePlanId ?? null,
          subscription_status_raw: subscriptionStatus ?? null,
          platform_plan_price: planPrice,
          admin_access,
          is_trialing: subStatusLower === 'trialing' || admin_access?.kind === 'trial',
        };
      });
    } catch (error) {
      console.error('Error fetching businesses:', error);
      return [];
    }
  }

  /**
   * Set or clear the Nexus-hosted website URL for a business (super admin).
   */
  static async updateNexusWebsiteUrl(
    businessId: string,
    url: string | null
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }
    const trimmed = url?.trim() ?? '';
    const normalized = trimmed === '' ? null : trimmed;
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          nexus_website_url: normalized,
          updated_at: new Date().toISOString(),
        })
        .eq('id', businessId);

      if (error) {
        console.error('Error updating nexus_website_url:', error);
        return { success: false, message: error.message };
      }
      return { success: true, message: 'Website URL saved' };
    } catch (e) {
      console.error('updateNexusWebsiteUrl:', e);
      return {
        success: false,
        message: e instanceof Error ? e.message : 'Failed to save URL',
      };
    }
  }

  /**
   * Set or clear the Vercel project ID backing a business's Nexus site.
   * Linking a project is what enables real pause/unpause enforcement.
   */
  static async updateVercelProjectId(
    businessId: string,
    projectId: string | null
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }
    const trimmed = projectId?.trim() ?? '';
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          vercel_project_id: trimmed === '' ? null : trimmed,
          updated_at: new Date().toISOString(),
        })
        .eq('id', businessId);
      if (error) {
        console.error('Error updating vercel_project_id:', error);
        return { success: false, message: error.message };
      }
      return { success: true, message: 'Vercel project saved' };
    } catch (e) {
      return {
        success: false,
        message: e instanceof Error ? e.message : 'Failed to save Vercel project',
      };
    }
  }

  /**
   * Link a Vercel project to a business and auto-fill the website URL from the
   * project's connected domains (custom domain preferred over *.vercel.app).
   */
  static async linkVercelProject(
    businessId: string,
    projectId: string
  ): Promise<{
    success: boolean;
    message: string;
    websiteUrl?: string | null;
    projectName?: string | null;
    /** Site state after the immediate post-link enforcement sync (null if sync failed). */
    paused?: boolean | null;
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }
    const trimmed = projectId.trim();
    if (!trimmed) {
      const cleared = await this.updateVercelProjectId(businessId, null);
      return { ...cleared, websiteUrl: null };
    }
    try {
      // Inspect first so a typo'd project ID doesn't get saved.
      const { data, error } = await supabase.functions.invoke('vercel-hosting-control', {
        body: { businessId, action: 'domains', projectId: trimmed },
      });
      if (error) return { success: false, message: error.message };
      if (data?.error) return { success: false, message: data.error };

      const saved = await this.updateVercelProjectId(businessId, trimmed);
      if (!saved.success) return saved;

      const suggestedUrl = (data?.suggestedUrl as string | null) || null;
      if (suggestedUrl) {
        const urlSaved = await this.updateNexusWebsiteUrl(businessId, suggestedUrl);
        if (!urlSaved.success) {
          return {
            success: true,
            message: `Project linked, but URL save failed: ${urlSaved.message}`,
            websiteUrl: null,
            projectName: (data?.projectName as string | null) || null,
          };
        }
      }

      // Enforce immediately: a business without an active hosting subscription
      // gets paused right away instead of waiting for the nightly sweep.
      const sync = await this.setVercelHostingState(businessId, 'sync');
      const paused = sync.success && !sync.skipped ? sync.paused === true : null;

      const urlPart = suggestedUrl
        ? `site URL set to ${suggestedUrl}`
        : 'no domains found, set the URL manually';
      const statePart =
        paused === true
          ? 'site PAUSED (no active hosting subscription)'
          : paused === false
            ? 'site live'
            : `state sync failed: ${sync.message}`;
      return {
        success: true,
        message: `Linked ${data?.projectName || trimmed} — ${urlPart} — ${statePart}`,
        websiteUrl: suggestedUrl,
        projectName: (data?.projectName as string | null) || null,
        paused,
      };
    } catch (e) {
      return {
        success: false,
        message: e instanceof Error ? e.message : 'Failed to link Vercel project',
      };
    }
  }

  /**
   * Email (and SMS when configured) the customer a link to activate hosting
   * for their finished website.
   */
  static async sendHostingActivationLink(
    businessId: string
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }
    try {
      const { data, error } = await supabase.functions.invoke('send-hosting-activation-link', {
        body: { businessId },
      });
      if (error) return { success: false, message: error.message };
      if (data?.error) return { success: false, message: data.error };
      const via = [data?.emailSent ? `email (${data?.sentTo})` : null, data?.smsSent ? 'SMS' : null]
        .filter(Boolean)
        .join(' and ');
      return { success: true, message: `Activation link sent via ${via || 'email'}` };
    } catch (e) {
      return {
        success: false,
        message: e instanceof Error ? e.message : 'Failed to send activation link',
      };
    }
  }

  /**
   * Control the live state of a business's Vercel site.
   * 'sync' applies the state implied by the hosting subscription status;
   * 'pause'/'unpause' are manual super admin overrides.
   */
  static async setVercelHostingState(
    businessId: string,
    action: 'sync' | 'pause' | 'unpause'
  ): Promise<{ success: boolean; paused?: boolean; skipped?: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }
    try {
      const { data, error } = await supabase.functions.invoke('vercel-hosting-control', {
        body: { businessId, action },
      });
      if (error) return { success: false, message: error.message };
      if (data?.error) return { success: false, message: data.error };
      if (data?.skipped) {
        return { success: true, skipped: true, message: data.reason || 'No Vercel project linked' };
      }
      return {
        success: true,
        paused: data?.paused === true,
        message: data?.paused ? 'Site paused on Vercel' : 'Site live on Vercel',
      };
    } catch (e) {
      return {
        success: false,
        message: e instanceof Error ? e.message : 'Failed to update site state',
      };
    }
  }

  /**
   * Get business details by ID
   */
  static async getBusinessDetails(businessId: string): Promise<any> {
    if (!supabase) return null;

    try {
      console.log('🏢 Fetching business details for:', businessId);
      
      const { data: business, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', businessId)
        .single();

      if (error) {
        console.error('❌ Error fetching business details:', error);
        throw error;
      }

      // Get owner information separately
      let owner = null;
      if (business.owner_id) {
        const { data: ownerData, error: ownerError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, created_at')
          .eq('id', business.owner_id)
          .single();
          
        if (!ownerError && ownerData) {
          owner = ownerData;
        }
      }

      // Get additional business data
      const [
        { count: activeResourceCount },
        { count: bookingCount },
        { count: serviceCount },
        { count: customerCount },
        { data: bookingsData }
      ] = await Promise.all([
        supabase.from('resources').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('status', 'active'),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('is_deleted', false),
        supabase.from('services').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('bookings').select('subtotal, discount_amount, status').eq('business_id', businessId).eq('is_deleted', false).neq('status', 'cancelled')
      ]);

      // Calculate total sales
      const salesAmount = (bookingsData || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // Get plan name from plan ID
      const getPlanName = (planId: string | null): string => {
        if (!planId) return 'No Plan';
        switch (planId) {
          case 'surge': return 'Surge Plan';
          case 'core': return 'Core Plan';
          case 'scale': return 'Scale Plan';
          case 'essential': return 'Essential Plan';
          case 'professional': return 'Professional Plan';
          default: return `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`;
        }
      };

      console.log('✅ Business details loaded successfully');

      return {
        ...business,
        users: owner, // For compatibility with existing code
        planName: getPlanName(business.active_plan_id),
        metrics: {
          userCount: activeResourceCount || 0,
          bookingCount: bookingCount || 0,
          serviceCount: serviceCount || 0,
          customerCount: customerCount || 0,
          salesAmount: salesAmount
        }
      };
    } catch (error) {
      console.error('❌ Error fetching business details:', error);
      return null;
    }
  }

  /**
   * Get all requests (super admin only). Used by Super Admin Dashboard Requests tab.
   */
  static async getRequests(): Promise<SuperAdminRequest[]> {
    if (!supabase) return [];

    try {
      const { data: rows, error } = await supabase
        .from('super_admin_requests')
        .select('id, business_id, user_id, source, status, notes, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching super admin requests:', error);
        return [];
      }

      const businessIds = [...new Set((rows || []).map((r: any) => r.business_id))];
      const { data: businesses } = await supabase
        .from('businesses')
        .select('id, name')
        .in('id', businessIds);

      const nameById = (businesses || []).reduce((acc: Record<string, string>, b: any) => {
        acc[b.id] = b.name || '';
        return acc;
      }, {});

      return (rows || []).map((r: any) => ({
        ...r,
        business_name: nameById[r.business_id] || '',
      }));
    } catch (error) {
      console.error('❌ Error fetching super admin requests:', error);
      return [];
    }
  }

  /**
   * Get all users across all businesses
   */
  static async getAllUsers(): Promise<UserSummary[]> {
    if (!supabase) return [];

    try {
      const { data: users, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          businesses(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return users?.map(user => {
        const business = Array.isArray(user.businesses) ? user.businesses[0] : user.businesses;
        return {
          id: user.id,
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: business?.name || 'No Business',
          business_id: business?.id || '',
          created_at: user.created_at,
          last_sign_in_at: '', // Would need to get from auth.users
          is_business_owner: false // Would need to check businesses.owner_id
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching all users:', error);
      return [];
    }
  }

  /**
   * Get recent users across all businesses
   */
  static async getRecentUsers(limit: number = 10): Promise<UserSummary[]> {
    if (!supabase) return [];

    try {
      const { data: users, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          businesses(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return users?.map(user => {
        const business = Array.isArray(user.businesses) ? user.businesses[0] : user.businesses;
        return {
          id: user.id,
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: business?.name || 'No Business',
          business_id: business?.id || '',
          created_at: user.created_at,
          last_sign_in_at: '', // Would need to get from auth.users
          is_business_owner: false // Would need to check businesses.owner_id
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching recent users:', error);
      return [];
    }
  }

  /**
   * Search for businesses or users
   */
  static async searchPlatform(query: string): Promise<{
    businesses: BusinessSummary[];
    users: UserSummary[];
  }> {
    if (!supabase || !query.trim()) {
      return { businesses: [], users: [] };
    }

    try {
      // Search businesses
      const { data: businesses } = await supabase
        .from('businesses')
        .select(`
          id,
          name,
          type,
          created_at,
          updated_at,
          owner_id,
          subscription_status,
          active_plan_id,
          twilio_enabled,
          twilio_account_sid,
          twilio_auth_token,
          VITE_TWILIO_PHONE_NUMBER,
          stripe_connect_account_id,
          stripe_connect_onboarding_completed,
          booking_settings,
          nexus_website_url,
          nexus_hosting_stripe_subscription_id,
          nexus_hosting_subscription_status,
          users!businesses_owner_id_fkey(
            email,
            first_name,
            last_name
          )
        `)
        .or(`name.ilike.%${query}%,type.ilike.%${query}%`)
        .limit(10);

      // Get Gmail connections and SMTP configs for searched businesses
      const searchedBusinessIds = (businesses || []).map(b => b.id);
      const [
        { data: searchGmailConnections },
        { data: searchSmtpConfigs }
      ] = await Promise.all([
        supabase
          .from('gmail_connections')
          .select('business_id')
          .eq('is_active', true)
          .in('business_id', searchedBusinessIds),
        supabase
          .from('smtp_config')
          .select('business_id, use_oauth, oauth_access_token')
          .in('business_id', searchedBusinessIds)
      ]);

      // Create sets for quick lookup
      const searchGmailConnectedBusinessIds = new Set(searchGmailConnections?.map(gc => gc.business_id) || []);
      const searchSmtpOauthBusinessIds = new Set(
        searchSmtpConfigs?.filter(sc => sc.use_oauth && sc.oauth_access_token).map(sc => sc.business_id) || []
      );
      
      // Debug logging for search integration detection
      console.log('🔍 Search integration detection:', {
        searchedBusinessIds,
        searchGmailConnections: searchGmailConnections?.length || 0,
        searchSmtpConfigs: searchSmtpConfigs?.length || 0,
        searchGmailConnectedBusinessIds: Array.from(searchGmailConnectedBusinessIds),
        searchSmtpOauthBusinessIds: Array.from(searchSmtpOauthBusinessIds)
      });

      // Search users
      const { data: users } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          businesses(
            id,
            name
          )
        `)
        .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .limit(10);



      // Transform business data
      const businessSummaries: BusinessSummary[] = (businesses || []).map(business => {
        const owner = Array.isArray(business.users) ? business.users[0] : business.users;
        
        // Get plan name from plan ID
        const getPlanName = (planId: string | null): string => {
          if (!planId) return 'No Plan';
          switch (planId) {
            case 'surge': return 'Surge Plan';
            case 'core': return 'Core Plan';
            case 'scale': return 'Scale Plan';
            case 'essential': return 'Essential Plan';
            case 'professional': return 'Professional Plan';
            default: return `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`;
          }
        };
        
        const status = mapSubscriptionToSuperAdminBusinessStatus(
          (business as { subscription_status?: string | null }).subscription_status,
          (business as { active_plan_id?: string | null }).active_plan_id,
        );
        
        return {
          id: business.id,
          name: business.name,
          owner_id: (business as any).owner_id || '',
          owner_email: owner?.email || '',
          owner_name: owner ? `${owner.first_name} ${owner.last_name}`.trim() : '',
          type: getPlanName(business.active_plan_id),
          plan_name: getPlanName(business.active_plan_id),
          addon_apps: [],
          nexus_monthly: 0,
          hosting_monthly: 0,
          monthly_cost: 0,
          created_at: business.created_at,
          user_count: 0,
          booking_count: 0,
          sales_amount: 0,
          last_activity: business.updated_at || business.created_at,
          status,
          frozen: false,
          integrations: {
            gmail: searchGmailConnectedBusinessIds.has(business.id) || searchSmtpOauthBusinessIds.has(business.id),
            twilio: !!(business.twilio_enabled && 
                      business.twilio_account_sid && 
                      business.twilio_auth_token && 
                      business.VITE_TWILIO_PHONE_NUMBER),
            stripe: !!(business.stripe_connect_account_id && 
                      business.stripe_connect_onboarding_completed)
          },
          logo_url: (business as any).booking_settings?.logo_url || undefined,
          nexus_website_url: (business as { nexus_website_url?: string | null }).nexus_website_url ?? null,
          nexus_hosting_stripe_subscription_id:
            (business as { nexus_hosting_stripe_subscription_id?: string | null }).nexus_hosting_stripe_subscription_id ??
            null,
          nexus_hosting_subscription_status:
            (business as { nexus_hosting_subscription_status?: string | null }).nexus_hosting_subscription_status ??
            null,
        };
      });

      // Transform user data
      const userSummaries: UserSummary[] = (users || []).map(user => {
        const business = Array.isArray(user.businesses) ? user.businesses[0] : user.businesses;
        return {
          id: user.id,
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: business?.name || 'No Business',
          business_id: business?.id || '',
          created_at: user.created_at,
          last_sign_in_at: '',
          is_business_owner: false
        };
      });

      return {
        businesses: businessSummaries,
        users: userSummaries
      };
    } catch (error) {
      console.error('Error searching platform:', error);
      return { businesses: [], users: [] };
    }
  }

  /**
   * Get business deletion info (what data will be affected)
   */
  static async getBusinessDeletionInfo(businessId: string): Promise<{
    canDelete: boolean;
    warnings: string[];
    counts: {
      users: number;
      bookings: number;
      customers: number;
      services: number;
      resources: number;
    };
    otherBusinessesForOwner?: { id: string; name: string }[];
    willPurgeAuth?: boolean;
  } | null> {
    if (!supabase) return null;

    try {
      console.log('🔍 Checking deletion requirements for business:', businessId);

      // Owner lives on businesses.owner_id (public.users often has no business_id).
      const { data: bizMeta } = await supabase
        .from('businesses')
        .select('owner_id, email, name')
        .eq('id', businessId)
        .maybeSingle();

      if (!bizMeta) {
        return {
          canDelete: false,
          warnings: ['Business not found — nothing to delete.'],
          counts: { users: 0, bookings: 0, customers: 0, services: 0, resources: 0 },
        };
      }

      const [
        { count: bookingCount },
        { count: customerCount },
        { count: serviceCount },
        { count: resourceCount }
      ] = await Promise.all([
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('is_deleted', false),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('services').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('resources').select('*', { count: 'exact', head: true }).eq('business_id', businessId)
      ]);

      // Other businesses that share this owner — MUST NOT delete their login/user rows.
      let otherBusinessesForOwner: { id: string; name: string }[] = [];
      if (bizMeta.owner_id) {
        const { data: otherBiz } = await supabase
          .from('businesses')
          .select('id, name')
          .eq('owner_id', bizMeta.owner_id)
          .neq('id', businessId);
        otherBusinessesForOwner = (otherBiz || []).map((b) => ({
          id: b.id,
          name: b.name || 'Unnamed business',
        }));
      }

      const willPurgeAuth = otherBusinessesForOwner.length === 0;
      const userCount = willPurgeAuth && bizMeta.owner_id ? 1 : 0;

      const counts = {
        users: userCount,
        bookings: bookingCount || 0,
        customers: customerCount || 0,
        services: serviceCount || 0,
        resources: resourceCount || 0
      };

      const warnings: string[] = [];
      const canDelete = true;

      if (counts.bookings > 0) {
        warnings.push(`${counts.bookings} booking(s) will be permanently deleted`);
      }
      if (counts.customers > 0) {
        warnings.push(`${counts.customers} customer(s) will be permanently deleted`);
      }
      if (counts.services > 0) {
        warnings.push(`${counts.services} service(s) will be permanently deleted`);
      }
      if (counts.resources > 0) {
        warnings.push(`${counts.resources} resource(s) will be permanently deleted`);
      }
      if (willPurgeAuth) {
        warnings.push('Owner login/auth account will be deleted (email can sign up again)');
      } else {
        warnings.push(
          `Owner login will be KEPT — they still own ${otherBusinessesForOwner.length} other business(es): ${otherBusinessesForOwner.map((b) => b.name).join(', ')}`,
        );
      }

      if (warnings.length === 0) {
        warnings.push('No associated data found. Business can be safely deleted.');
      }

      console.log('✅ Deletion info gathered:', { canDelete, warnings, counts, otherBusinessesForOwner, willPurgeAuth });

      return { canDelete, warnings, counts, otherBusinessesForOwner, willPurgeAuth };
    } catch (error) {
      console.error('❌ Error checking deletion requirements:', error);
      return null;
    }
  }

  /**
   * Delete a business and all its associated data
   */
  static async deleteBusiness(businessId: string, confirmDeletion: boolean = false): Promise<{
    success: boolean;
    message: string;
    deletedCounts?: {
      users: number;
      bookings: number;
      customers: number;
      services: number;
      resources: number;
    };
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    if (!confirmDeletion) {
      return { success: false, message: 'Deletion must be explicitly confirmed' };
    }

    try {
      console.log('🗑️ Starting business deletion process for:', businessId);

      // Get deletion info first
      const deletionInfo = await this.getBusinessDeletionInfo(businessId);
      if (!deletionInfo) {
        return { success: false, message: 'Could not verify business deletion requirements' };
      }

      // HARD GUARD: never touch another business. Only this id.
      if (!businessId || typeof businessId !== 'string') {
        return { success: false, message: 'Invalid business id' };
      }

      const { data: bizRow, error: bizLookupErr } = await supabase
        .from('businesses')
        .select('id, owner_id, email, name')
        .eq('id', businessId)
        .maybeSingle();
      if (bizLookupErr || !bizRow?.id) {
        return { success: false, message: 'Business not found' };
      }

      const ownerId = bizRow.owner_id as string | undefined;

      // Other businesses owned by the same account — NEVER delete their owner/auth.
      let otherBusinessesForOwner: { id: string; name: string }[] = [];
      if (ownerId) {
        const { data: otherBiz } = await supabase
          .from('businesses')
          .select('id, name')
          .eq('owner_id', ownerId)
          .neq('id', businessId);
        otherBusinessesForOwner = otherBiz || [];
      }
      const ownerStillOwnsOtherBusinesses = otherBusinessesForOwner.length > 0;

      // Auth/user purge candidates — ONLY for this business, and only if safe.
      const emailsToPurge: string[] = [];
      const authIdsToPurge: string[] = [];
      if (!ownerStillOwnsOtherBusinesses) {
        if (bizRow.email) emailsToPurge.push(String(bizRow.email).trim().toLowerCase());
        if (ownerId) {
          authIdsToPurge.push(ownerId);
          const { data: ownerUser } = await supabase
            .from('users')
            .select('email')
            .eq('id', ownerId)
            .maybeSingle();
          if (ownerUser?.email) {
            emailsToPurge.push(String(ownerUser.email).trim().toLowerCase());
          }
        }
      } else {
        console.warn(
          '⚠️ Owner still owns other businesses — skipping owner/auth purge:',
          otherBusinessesForOwner.map((b) => b.name),
        );
      }

      // Resource logins that belong ONLY to this business (not used as owner elsewhere).
      try {
        const { data: resourceAuth } = await supabase
          .from('resources')
          .select('auth_user_id, email')
          .eq('business_id', businessId);
        for (const r of resourceAuth || []) {
          if (!r.auth_user_id) continue;
          // Never purge an auth id that still owns another business
          const { count: ownedElsewhere } = await supabase
            .from('businesses')
            .select('id', { count: 'exact', head: true })
            .eq('owner_id', r.auth_user_id)
            .neq('id', businessId);
          if ((ownedElsewhere || 0) > 0) continue;
          authIdsToPurge.push(r.auth_user_id);
          if (r.email) emailsToPurge.push(String(r.email).trim().toLowerCase());
        }
      } catch {
        /* ignore */
      }

      const deletedCounts = { ...deletionInfo.counts };

      // 1. Delete booking-related data scoped to THIS business only
      if (deletionInfo.counts.bookings > 0) {
        console.log('🗑️ Deleting bookings and related data for', businessId);
        const tables = ['booking_items', 'time_entries', 'booking_notes', 'payments'];
        for (const table of tables) {
          try {
            await supabase.from(table).delete().eq('business_id', businessId);
          } catch {
            /* table may not exist */
          }
        }
        const { error: bookingsError } = await supabase
          .from('bookings')
          .delete()
          .eq('business_id', businessId);
        if (bookingsError) {
          throw new Error(`Failed to delete bookings: ${bookingsError.message}`);
        }
      }

      // 2. Delete other business-scoped tables (always filter by business_id)
      const businessTables = [
        'automations',
        'automation_steps',
        'proposals',
        'invoices',
        'customers',
        'vehicles',
        'services',
        'resources',
        'materials',
        'business_settings',
        'invoice_settings',
        'email_templates',
        'sms_templates',
        'time_entries',
        'payroll_entries',
      ];

      for (const table of businessTables) {
        try {
          const { error } = await supabase
            .from(table)
            .delete()
            .eq('business_id', businessId);
          if (error && !error.message.includes('does not exist')) {
            console.error(`❌ Error deleting ${table}:`, error);
          }
        } catch {
          /* ignore missing tables */
        }
      }

      try {
        await supabase.from('business_users').delete().eq('business_id', businessId);
      } catch {
        /* optional */
      }

      // Users keyed to this business_id only (never global email wipe)
      try {
        await supabase.from('users').delete().eq('business_id', businessId);
      } catch {
        /* business_id may not exist on users */
      }

      // 3. Delete the business row FIRST (while owner row still exists → satisfies FK).
      console.log('🗑️ Deleting business row', businessId, bizRow.name);
      const { data: deletedBiz, error: businessError } = await supabase
        .from('businesses')
        .delete()
        .eq('id', businessId)
        .select('id');

      if (businessError) {
        throw new Error(`Failed to delete business: ${businessError.message}`);
      }
      if (!deletedBiz?.length) {
        throw new Error('Business delete matched 0 rows — aborting user/auth cleanup');
      }

      // 4. Only now remove owner public.users + auth — and ONLY if they own nothing else.
      if (ownerId && !ownerStillOwnsOtherBusinesses) {
        // Re-check after delete (race-safe)
        const { count: stillOwns } = await supabase
          .from('businesses')
          .select('id', { count: 'exact', head: true })
          .eq('owner_id', ownerId);
        if ((stillOwns || 0) === 0) {
          console.log('🗑️ Deleting owner public.users row (no other businesses)');
          const { error: ownerUserError } = await supabase
            .from('users')
            .delete()
            .eq('id', ownerId);
          if (ownerUserError) {
            console.error('❌ Error deleting owner user row:', ownerUserError);
          }
        } else {
          console.warn('⚠️ Skipping owner user delete — still owns', stillOwns, 'business(es)');
          authIdsToPurge.length = 0;
          emailsToPurge.length = 0;
        }
      }

      // 5. Purge auth only for ids/emails that are safe (no remaining owned businesses)
      const safeAuthIds = [...new Set(authIdsToPurge)].filter(Boolean);
      const safeEmails = [...new Set(emailsToPurge)].filter((e) => /^\S+@\S+\.\S+$/.test(e));

      if (safeAuthIds.length > 0 || safeEmails.length > 0) {
        // Final safety: drop any auth id that still owns a business
        const idsToPurge: string[] = [];
        for (const id of safeAuthIds) {
          const { count } = await supabase
            .from('businesses')
            .select('id', { count: 'exact', head: true })
            .eq('owner_id', id);
          if ((count || 0) === 0) idsToPurge.push(id);
          else console.warn('⚠️ Skipping auth purge for', id, '— still owns a business');
        }

        if (idsToPurge.length > 0 || safeEmails.length > 0) {
          try {
            console.log('🗑️ Purging auth users for deleted business...', {
              userIds: idsToPurge,
              emails: ownerStillOwnsOtherBusinesses ? [] : safeEmails,
            });
            // Never purge by email if owner still had other businesses (belt + suspenders)
            const emailsForPurge = ownerStillOwnsOtherBusinesses ? [] : safeEmails;
            const { data: purgeData, error: purgeError } = await supabase.functions.invoke(
              'super-admin-purge-auth-users',
              {
                body: {
                  userIds: idsToPurge,
                  emails: emailsForPurge,
                  // Require edge function to re-check ownership before delete
                  requireNoOwnedBusinesses: true,
                },
              },
            );
            if (purgeError) {
              console.error('❌ Auth purge edge function failed:', purgeError);
              const { error: rpcError } = await supabase.rpc('super_admin_purge_auth_users', {
                p_user_ids: idsToPurge,
                p_emails: emailsForPurge,
              });
              if (rpcError) {
                console.error('❌ Auth purge RPC fallback failed:', rpcError);
                return {
                  success: true,
                  message:
                    'Business deleted, but auth account cleanup failed — the email may still show as in use.',
                  deletedCounts,
                };
              }
            } else {
              console.log('✅ Auth purge result:', purgeData);
            }
          } catch (purgeErr) {
            console.error('❌ Auth purge threw:', purgeErr);
            return {
              success: true,
              message:
                'Business deleted, but auth account cleanup failed — the email may still show as in use.',
              deletedCounts,
            };
          }
        }
      }

      console.log('✅ Business deletion completed successfully');

      return {
        success: true,
        message: ownerStillOwnsOtherBusinesses
          ? `Business deleted. Owner login kept because they still own: ${otherBusinessesForOwner.map((b) => b.name).join(', ')}`
          : 'Business, associated data, and auth accounts deleted successfully',
        deletedCounts,
      };

    } catch (error) {
      console.error('❌ Error during business deletion:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred during deletion'
      };
    }
  }

  /**
   * Impersonate a business owner (for super admin use)
   */
  static async impersonateBusinessOwner(businessId: string): Promise<{
    success: boolean;
    message: string;
    ownerData?: any;
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('👤 Starting impersonation for business:', businessId);

      // Get business information
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id, name, owner_id')
        .eq('id', businessId)
        .single();

      if (businessError || !business) {
        console.error('❌ Business not found:', businessError);
        return { success: false, message: 'Business not found' };
      }

      console.log('🏢 Business found:', business.name, 'Owner ID:', business.owner_id);

      let owner = null;

      // Try to find owner by owner_id first
      if (business.owner_id) {
        const { data: ownerData, error: ownerError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, business_id')
          .eq('id', business.owner_id)
          .single();

        if (!ownerError && ownerData) {
          owner = ownerData;
          console.log('✅ Found owner by owner_id:', owner.email);
        } else {
          console.log('⚠️ Owner not found by owner_id, trying fallback...');
        }
      }

      // Fallback: Find any user associated with this business (first user found)
      if (!owner) {
        console.log('🔍 Looking for any user associated with business...');
        
        const { data: businessUsers, error: usersError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, business_id')
          .eq('business_id', businessId)
          .limit(1);

        if (!usersError && businessUsers && businessUsers.length > 0) {
          owner = businessUsers[0];
          console.log('✅ Found fallback user:', owner.email);
        } else {
          console.error('❌ No users found for business:', usersError);
        }
      }

      // Final fallback: Create a temporary user session for this business
      if (!owner) {
        console.log('🆘 No users found, creating temporary admin session...');
        
        // Create a minimal user object for impersonation
        owner = {
          id: `temp_admin_${businessId}`,
          email: `admin@${business.name.toLowerCase().replace(/\s+/g, '')}.temp`,
          first_name: 'Temp',
          last_name: 'Admin',
          business_id: businessId
        };
        
        console.log('✅ Created temporary admin session:', owner.email);
      }

      return {
        success: true,
        message: 'Owner data retrieved successfully',
        ownerData: {
          id: owner.id,
          email: owner.email,
          first_name: owner.first_name,
          last_name: owner.last_name,
          business_id: businessId, // Ensure we use the correct business ID
          business_name: business.name
        }
      };

    } catch (error) {
      console.error('❌ Error during impersonation setup:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Delete a user completely from both auth.users and public.users
   */
  static async deleteUser(userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('🗑️ Starting user deletion for:', userId);

      // First, get the user's email before deletion
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('email')
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        console.error('❌ User not found:', userError);
        return { success: false, message: 'User not found' };
      }

      const userEmail = userData.email;
      console.log('📧 User email:', userEmail);

      // Delete from public.users first
      const { error: publicDeleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (publicDeleteError) {
        console.error('❌ Error deleting from public.users:', publicDeleteError);
        return { success: false, message: `Failed to delete user: ${publicDeleteError.message}` };
      }

      console.log('✅ Deleted from public.users');

      // Delete from auth.users using RPC (requires service role)
      try {
        const { error: authDeleteError } = await supabase.rpc('delete_auth_user', {
          user_id: userId
        });

        if (authDeleteError) {
          console.error('❌ Error deleting from auth.users:', authDeleteError);
          // Don't fail completely, just log the error
          console.log('⚠️ Could not delete from auth.users, but user is removed from public.users');
        } else {
          console.log('✅ Deleted from auth.users');
        }
      } catch (error) {
        console.log('⚠️ RPC function not available, auth user may still exist');
      }

      // Also delete from user_setup_status if exists
      try {
        await supabase
          .from('user_setup_status')
          .delete()
          .eq('user_id', userId);
        console.log('✅ Deleted from user_setup_status');
      } catch (error) {
        console.log('ℹ️ user_setup_status table might not exist or user not found');
      }

      console.log('✅ User deletion completed successfully');
      return {
        success: true,
        message: `User ${userEmail} deleted successfully`
      };

    } catch (error) {
      console.error('❌ Error during user deletion:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred during deletion'
      };
    }
  }

  /**
   * List subscription plans for super-admin grant access UI.
   */
  static async listSubscriptionPlansForGrant(): Promise<SubscriptionPlanOption[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('subscription_plans')
      .select('plan_id, plan_name, plan_price, included_apps')
      .order('plan_price', { ascending: false });

    if (error) {
      console.error('Error loading subscription plans:', error);
      return [];
    }

    return (data || [])
      .filter((plan) => plan.plan_id !== 'nexus-hosting')
      .map((plan) => ({
      plan_id: plan.plan_id,
      plan_name: plan.plan_name,
      plan_price: typeof plan.plan_price === 'number' ? plan.plan_price : null,
      included_app_count: Array.isArray(plan.included_apps) ? plan.included_apps.length : 0,
    }));
  }

  /**
   * Grant complimentary or trial access — activates account, assigns plan, installs apps.
   */
  static async grantBusinessAccess(
    params: GrantBusinessAccessParams,
  ): Promise<GrantBusinessAccessResult> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('grant-business-access', {
        body: {
          businessId: params.businessId,
          planId: params.planId,
          accessMode: params.accessMode,
          trialEndDate: params.trialEndDate,
          installAllApps: params.installAllApps !== false,
          notes: params.notes,
        },
      });

      if (error) {
        console.error('Error granting business access:', error, data);
        const responseMessage =
          (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
            ? data.error
            : null) ||
          (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
            ? data.message
            : null);
        return {
          success: false,
          message:
            responseMessage ||
            error.message ||
            'Failed to grant access. Deploy the grant-business-access edge function if you have not already.',
        };
      }

      if (!data?.success) {
        return {
          success: false,
          message: data?.error || data?.message || 'Failed to grant access',
        };
      }

      return {
        success: true,
        message: data.message,
        planId: data.planId,
        planName: data.planName,
        appsInstalled: data.appsInstalled,
        accessMode: data.accessMode,
      };
    } catch (error) {
      console.error('Error in grantBusinessAccess:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * End a Stripe trial immediately and start billing — the customer's card on
   * file is charged the first invoice right away.
   */
  static async startTrialSubscriptionNow(
    businessId: string,
  ): Promise<{ success: boolean; message: string; subscriptionStatus?: string; invoiceStatus?: string | null }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('super-admin-start-subscription', {
        body: { businessId },
      });

      if (error) {
        console.error('Error starting subscription:', error, data);
        // supabase-js surfaces non-2xx as a generic FunctionsHttpError; the
        // real reason (no card on file, not trialing, etc.) is in the response body.
        let responseMessage =
          (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
            ? data.error
            : null);
        if (!responseMessage && error && typeof error === 'object' && 'context' in error) {
          try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
              const body = await ctx.json();
              if (body && typeof body.error === 'string') responseMessage = body.error;
            }
          } catch {
            // fall through to generic message
          }
        }
        return {
          success: false,
          message: responseMessage || error.message || 'Failed to start subscription',
        };
      }

      if (!data?.success) {
        return { success: false, message: data?.error || 'Failed to start subscription' };
      }

      return {
        success: true,
        message: data.message,
        subscriptionStatus: data.subscriptionStatus,
        invoiceStatus: data.invoiceStatus,
      };
    } catch (error) {
      console.error('Error in startTrialSubscriptionNow:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Enable trial for a business
   */
  static async enableTrial(
    businessId: string,
    trialEndDate: string,
    adminUserId: string,
  ): Promise<{ success: boolean; message: string }> {
    void adminUserId;
    const result = await this.grantBusinessAccess({
      businessId,
      accessMode: 'trial',
      trialEndDate,
      installAllApps: true,
    });
    return { success: result.success, message: result.message };
  }

  /**
   * Get trial audit log for a business
   */
  static async getTrialAuditLog(businessId: string) {
    if (!supabase) return [];

    try {
      const { data, error } = await supabase
        .from('trial_audit_log')
        .select(`
          *,
          admin_user:auth.users!trial_audit_log_admin_user_id_fkey(email)
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching trial audit log:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching trial audit log:', error);
      return [];
    }
  }

  /**
   * Reset password for a business owner (send email)
   */
  static async resetPasswordForBusiness(email: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      console.log('🔑 Resetting password for business owner:', email);
      
      // Use Supabase's built-in password reset functionality
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password/update`
      });

      if (error) {
        console.error('❌ Error sending password reset email:', error);
        throw error;
      }

      console.log('✅ Password reset email sent successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  /**
   * Set password directly for a business owner
   * Uses Supabase Edge Function with service role permissions
   */
  static async setPasswordForBusiness(email: string, newPassword: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      console.log('🔑 Setting new password for business owner:', email);
      
      // Call the admin-reset-password edge function
      const { data, error } = await supabase.functions.invoke('admin-reset-password', {
        body: {
          email,
          newPassword
        }
      });

      if (error) {
        console.error('❌ Error calling edge function:', error);
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      console.log('✅ Password updated successfully:', data?.message);
    } catch (error) {
      console.error('Error setting password:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive subscription data for a business
   */
  static async getBusinessSubscriptionData(businessId: string): Promise<any> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      console.log('📊 Fetching subscription data for business:', businessId);

      // Get business subscription info
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('subscription_status, active_plan_id, stripe_subscription_id')
        .eq('id', businessId)
        .single();

      if (businessError) {
        console.error('❌ Error fetching business data:', businessError);
        throw businessError;
      }

      // Get plan details
      let planData = null;
      if (business.active_plan_id) {
        const { data: plan, error: planError } = await supabase
          .from('subscription_plans')
          .select('plan_id, plan_name, plan_price, included_apps')
          .eq('plan_id', business.active_plan_id)
          .single();

        if (!planError && plan) {
          planData = plan;
        }
      }

      // Get currently installed apps
      const { data: installedApps, error: appsError } = await supabase
        .from('business_apps_v2')
        .select(`
          app_id,
          status,
          installed_at,
          app_registry_v2 (
            id,
            name,
            type,
            price_monthly
          )
        `)
        .eq('business_id', businessId)
        .eq('status', 'active');

      if (appsError) {
        console.error('❌ Error fetching installed apps:', appsError);
        throw appsError;
      }

      // Get addon subscriptions
      const { data: addonSubscriptions, error: addonError } = await supabase
        .from('business_addon_subscriptions')
        .select(`
          addon_app_id,
          status,
          addon_apps (
            id,
            name,
            price_monthly
          )
        `)
        .eq('business_id', businessId)
        .eq('status', 'active');

      if (addonError) {
        console.error('❌ Error fetching addon subscriptions:', addonError);
        throw addonError;
      }

      // Get admin-granted apps
      const { data: adminGrantedApps, error: adminError } = await supabase
        .from('business_apps_v2')
        .select(`
          app_id,
          status,
          admin_granted_access,
          app_registry_v2 (
            id,
            name,
            type,
            price_monthly
          )
        `)
        .eq('business_id', businessId)
        .eq('status', 'active')
        .eq('admin_granted_access', true);

      if (adminError) {
        console.error('❌ Error fetching admin-granted apps:', adminError);
        throw adminError;
      }

      // Get all available apps
      const { data: allApps, error: allAppsError } = await supabase
        .from('app_registry_v2')
        .select('id, name, type, price_monthly')
        .order('name');

      if (allAppsError) {
        console.error('❌ Error fetching all apps:', allAppsError);
        throw allAppsError;
      }

      // Process the data
      const installedAppIds = installedApps?.map(app => app.app_id) || [];
      const addonAppIds = addonSubscriptions?.map(sub => sub.addon_apps?.id).filter(Boolean) || [];
      const planAppIds = planData?.included_apps || [];

      // Separate apps by type
      const includedApps = installedApps
        ?.filter(app => planAppIds.includes(app.app_id))
        ?.map(app => ({
          id: app.app_id,
          name: app.app_registry_v2?.name || 'Unknown App',
          type: app.app_registry_v2?.type || 'unknown'
        })) || [];

      const addonApps = addonSubscriptions
        ?.map(sub => ({
          id: sub.addon_app_id,
          name: sub.addon_apps?.name || 'Unknown App',
          price: sub.addon_apps?.price_monthly || 0
        })) || [];

      const adminGrantedAppsList = adminGrantedApps
        ?.map(app => ({
          id: app.app_id,
          name: app.app_registry_v2?.name || 'Unknown App',
          type: app.app_registry_v2?.type || 'unknown',
          price: app.app_registry_v2?.price_monthly || 0
        })) || [];

      // Find available apps (not currently installed)
      const availableApps = allApps
        ?.filter(app => !installedAppIds.includes(app.id))
        ?.map(app => ({
          id: app.id,
          name: app.name,
          type: app.type,
          price: app.price_monthly || 0,
          inPlan: planAppIds.includes(app.id)
        })) || [];

      const result = {
        planName: planData?.plan_name || 'No Plan',
        planPrice: planData?.plan_price || 0,
        status: business.subscription_status || 'unknown',
        includedApps,
        addonApps,
        adminGrantedApps: adminGrantedAppsList,
        availableApps,
        totalInstalledApps: installedAppIds.length,
        totalAddonApps: addonApps.length,
        totalAdminGrantedApps: adminGrantedAppsList.length,
        totalAvailableApps: availableApps.length
      };

      console.log('✅ Subscription data fetched successfully:', result);
      return result;

    } catch (error) {
      console.error('❌ Error fetching subscription data:', error);
      throw error;
    }
  }

  /**
   * Media (social) connection seats — FB / IG / Google Business.
   * Free seats let a business trial connections without paying.
   */
  static async getSocialSeatUsage(businessId: string): Promise<SocialSeatUsage> {
    if (!supabase) throw new Error('Supabase client not initialized');
    const { data, error } = await supabase.functions.invoke('super-admin-social-seats', {
      body: { action: 'get', businessId },
    });
    if (error || data?.error) {
      throw new Error(data?.error || error?.message || 'Failed to load media connection seats');
    }
    return data.usage as SocialSeatUsage;
  }

  /** Set the number of FREE media connection seats (admin trial grant, 0-50). */
  static async setFreeSocialSeats(businessId: string, freeSeats: number): Promise<SocialSeatUsage> {
    if (!supabase) throw new Error('Supabase client not initialized');
    const { data, error } = await supabase.functions.invoke('super-admin-social-seats', {
      body: { action: 'set', businessId, freeSeats },
    });
    if (error || data?.error) {
      throw new Error(data?.error || error?.message || 'Failed to update media connection seats');
    }
    return data.usage as SocialSeatUsage;
  }

  /**
   * Grant app access to a business (admin override)
   */
  static async grantAppAccess(businessId: string, appId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      console.log('🔑 Granting app access:', { businessId, appId });

      // Check if app already exists for this business
      const { data: existingApp, error: checkError } = await supabase
        .from('business_apps_v2')
        .select('id')
        .eq('business_id', businessId)
        .eq('app_id', appId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('❌ Error checking existing app:', checkError);
        throw checkError;
      }

      if (existingApp) {
        throw new Error('App is already installed for this business');
      }

      // Place the app at the next available grid position (lowest unused
      // slot, starting at 1) — same logic as the grant-business-access flow.
      const { data: gridApps, error: gridError } = await supabase
        .from('business_apps_v2')
        .select('visible_app_grid')
        .eq('business_id', businessId);

      if (gridError) {
        console.error('❌ Error loading grid positions:', gridError);
        throw gridError;
      }

      const usedPositions = new Set<number>(
        (gridApps || [])
          .map(app => app.visible_app_grid)
          .filter((pos): pos is number => pos != null)
      );
      let position = 1;
      while (usedPositions.has(position)) position++;

      // Insert the app with admin_granted_access = true
      const { error: insertError } = await supabase
        .from('business_apps_v2')
        .insert({
          business_id: businessId,
          app_id: appId,
          status: 'active',
          admin_granted_access: true,
          visible_app_grid: position,
          hidden_app_grid: null,
          installed_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('❌ Error granting app access:', insertError);
        throw insertError;
      }

      console.log('✅ App access granted successfully');
    } catch (error) {
      console.error('❌ Error in grantAppAccess:', error);
      throw error;
    }
  }

  /**
   * Remove admin-granted app access from a business
   */
  static async removeAppAccess(businessId: string, appId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      console.log('🗑️ Removing app access:', { businessId, appId });

      // Only remove apps that were granted by admin
      const { error: deleteError } = await supabase
        .from('business_apps_v2')
        .delete()
        .eq('business_id', businessId)
        .eq('app_id', appId)
        .eq('admin_granted_access', true);

      if (deleteError) {
        console.error('❌ Error removing app access:', deleteError);
        throw deleteError;
      }

      console.log('✅ App access removed successfully');
    } catch (error) {
      console.error('❌ Error in removeAppAccess:', error);
      throw error;
    }
  }

  /**
   * Freeze a business account
   */
  static async freezeBusiness(
    businessId: string, 
    reason?: string,
    adminUserId?: string
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('🧊 Freezing business account:', businessId);

      const now = new Date().toISOString();

      // Update business to frozen status
      const { error: updateError } = await supabase
        .from('businesses')
        .update({
          frozen: true,
          frozen_at: now,
          frozen_by: adminUserId || null,
          frozen_reason: reason || 'Account disabled by administrator',
          updated_at: now
        })
        .eq('id', businessId);

      if (updateError) {
        console.error('❌ Error freezing business:', updateError);
        throw updateError;
      }

      // Log the freeze action in audit table
      const { error: auditError } = await supabase
        .from('business_freeze_audit')
        .insert({
          business_id: businessId,
          action: 'freeze',
          admin_user_id: adminUserId || null,
          reason: reason || 'Account disabled by administrator'
        });

      if (auditError) {
        console.error('⚠️ Error logging freeze action:', auditError);
        // Don't fail the operation for audit logging issues
      }

      console.log('✅ Business frozen successfully');
      return {
        success: true,
        message: 'Business account frozen successfully'
      };

    } catch (error) {
      console.error('❌ Error freezing business:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to freeze business'
      };
    }
  }

  /**
   * Get users/accounts for a specific business.
   * Includes the business owner (from users table) and active resources that have auth accounts.
   */
  static async getBusinessUsers(businessId: string): Promise<BusinessUser[]> {
    if (!supabase) return [];

    try {
      const result: BusinessUser[] = [];
      const seenAuthIds = new Set<string>();

      // 1. Fetch the business owner
      let owner: { id: string; email: string; first_name: string; last_name: string } | null = null;

      const { data: business } = await supabase
        .from('businesses')
        .select('owner_id')
        .eq('id', businessId)
        .single();

      if (business?.owner_id) {
        // Try public.users first (requires super admin RLS policy)
        const { data: ownerData } = await supabase
          .from('users')
          .select('id, email, first_name, last_name')
          .eq('id', business.owner_id)
          .single();

        if (ownerData) owner = ownerData;
      }

      // Fallback: find the user linked to this business via users.business_id
      if (!owner) {
        const { data: fallbackUsers } = await supabase
          .from('users')
          .select('id, email, first_name, last_name')
          .eq('business_id', businessId)
          .limit(1);

        if (fallbackUsers && fallbackUsers.length > 0) {
          owner = fallbackUsers[0];
        }
      }

      // Fallback: resolve owner from the resources table (not blocked by users RLS)
      if (!owner && business?.owner_id) {
        const { data: ownerResource } = await supabase
          .from('resources')
          .select('id, email, first_name, last_name, auth_user_id')
          .eq('business_id', businessId)
          .eq('auth_user_id', business.owner_id)
          .single();

        if (ownerResource) {
          owner = {
            id: ownerResource.auth_user_id!,
            email: ownerResource.email || '',
            first_name: ownerResource.first_name || '',
            last_name: ownerResource.last_name || ''
          };
        }
      }

      if (owner) {
        seenAuthIds.add(owner.id);
        result.push({
          id: owner.id,
          email: owner.email || '',
          first_name: owner.first_name || '',
          last_name: owner.last_name || '',
          role: 'owner',
          auth_user_id: owner.id,
          status: 'active'
        });
      }

      // 2. Fetch active resources that have an auth account (can actually log in)
      const { data: resources, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name, email, role, auth_user_id, is_active')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .not('auth_user_id', 'is', null)
        .order('role', { ascending: true });

      if (error) {
        console.error('Error fetching business resources:', error);
        return result;
      }

      for (const r of resources || []) {
        if (r.auth_user_id && !seenAuthIds.has(r.auth_user_id)) {
          seenAuthIds.add(r.auth_user_id);
          result.push({
            id: r.id,
            email: r.email || '',
            first_name: r.first_name || '',
            last_name: r.last_name || '',
            role: r.role || 'employee',
            auth_user_id: r.auth_user_id,
            status: r.is_active ? 'active' : 'inactive'
          });
        }
      }

      return result;
    } catch (error) {
      console.error('Error fetching business users:', error);
      return [];
    }
  }

  /**
   * Create a remote session request
   */
  static async createRemoteSession(
    adminId: string,
    targetUserId: string,
    businessId: string
  ): Promise<{ success: boolean; session?: RemoteSession; message: string }> {
    if (!supabase) return { success: false, message: 'Database not available' };

    try {
      // Kill ALL existing non-ended sessions for this target user.
      // If the admin is reconnecting, the old session is either stuck or unwanted.
      await supabase
        .from('remote_sessions')
        .update({ status: 'ended', ended_at: new Date().toISOString(), ended_by: 'timeout' })
        .eq('target_user_id', targetUserId)
        .in('status', ['pending', 'approved', 'active', 'paused']);

      const sessionCode = Math.floor(100000 + Math.random() * 900000).toString();

      const { data, error } = await supabase
        .from('remote_sessions')
        .insert({
          admin_id: adminId,
          target_user_id: targetUserId,
          business_id: businessId,
          status: 'pending',
          session_code: sessionCode
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, session: data as RemoteSession, message: 'Session created' };
    } catch (error) {
      console.error('Error creating remote session:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create session'
      };
    }
  }

  /**
   * Update a remote session status (admin side)
   */
  static async endRemoteSession(sessionId: string, endedBy: 'admin' | 'user' | 'timeout' | 'app_closed' = 'admin'): Promise<boolean> {
    if (!supabase) return false;

    try {
      const { error } = await supabase
        .from('remote_sessions')
        .update({
          status: 'ended',
          ended_at: new Date().toISOString(),
          ended_by: endedBy
        })
        .eq('id', sessionId);

      return !error;
    } catch {
      return false;
    }
  }

  /**
   * Send a push notification to request remote access
   */
  static async sendRemoteAccessNotification(
    targetUserId: string,
    businessId: string,
    sessionCode: string
  ): Promise<boolean> {
    if (!supabase) return false;

    try {
      const { error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          notification: {
            title: 'Remote Access Request',
            body: `A super admin is requesting to view your screen. Code: ${sessionCode}`,
            data: {
              type: 'remote_access_request',
              session_code: sessionCode
            }
          },
          user_id: targetUserId,
          business_id: businessId
        }
      });

      return !error;
    } catch {
      return false;
    }
  }

  /**
   * Unfreeze a business account
   */
  static async unfreezeBusiness(
    businessId: string,
    adminUserId?: string
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('🔓 Unfreezing business account:', businessId);

      const now = new Date().toISOString();

      // Update business to unfrozen status
      const { error: updateError } = await supabase
        .from('businesses')
        .update({
          frozen: false,
          frozen_at: null,
          frozen_by: null,
          frozen_reason: null,
          updated_at: now
        })
        .eq('id', businessId);

      if (updateError) {
        console.error('❌ Error unfreezing business:', updateError);
        throw updateError;
      }

      // Log the unfreeze action in audit table
      const { error: auditError } = await supabase
        .from('business_freeze_audit')
        .insert({
          business_id: businessId,
          action: 'unfreeze',
          admin_user_id: adminUserId || null
        });

      if (auditError) {
        console.error('⚠️ Error logging unfreeze action:', auditError);
        // Don't fail the operation for audit logging issues
      }

      console.log('✅ Business unfrozen successfully');
      return {
        success: true,
        message: 'Business account unfrozen successfully'
      };

    } catch (error) {
      console.error('❌ Error unfreezing business:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to unfreeze business'
      };
    }
  }

  /**
   * Sync platform Stripe invoices/subscriptions into mirror tables.
   */
  static async syncStripeBillingMirror(options?: { businessId?: string; syncAll?: boolean }): Promise<StripeBillingSyncResult> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase.functions.invoke('sync-stripe-billing-mirror', {
      body: {
        businessId: options?.businessId,
        syncAll: options?.syncAll ?? !options?.businessId,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return {
      ok: !!data?.ok,
      invoicesSynced: data?.invoicesSynced ?? 0,
      subscriptionsSynced: data?.subscriptionsSynced ?? 0,
      message: data?.message,
    };
  }

  /**
   * List mirrored Stripe invoices for Super Admin.
   */
  static async listStripeInvoices(params?: {
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ invoices: StripeInvoiceMirrorRow[]; total: number }> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 50;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('stripe_invoices')
      .select('*, businesses(name)', { count: 'exact' })
      .order('invoice_created_at', { ascending: false });

    if (params?.status && params.status !== 'all') {
      query = query.eq('status', params.status);
    }

    const search = params?.search?.trim();
    if (search) {
      query = query.or(
        `number.ilike.%${search}%,customer_email.ilike.%${search}%,stripe_customer_id.ilike.%${search}%`,
      );
    }

    const { data, error, count } = await query.range(from, to);
    if (error) throw error;

    return {
      invoices: (data || []) as StripeInvoiceMirrorRow[],
      total: count ?? 0,
    };
  }

  /**
   * Apply a Stripe Dashboard–equivalent invoice action on the platform account, then sync mirror.
   */
  static async updateStripeInvoiceStatus(
    stripeInvoiceId: string,
    action: StripeInvoiceAdminAction,
  ): Promise<StripeInvoiceActionResult> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase.functions.invoke('super-admin-stripe-invoice', {
      body: { stripeInvoiceId, action },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return data as StripeInvoiceActionResult;
  }

  /**
   * Aggregate billing metrics from mirror tables for the Invoices tab.
   * Headline MRR prefers Stripe Analytics `revenue.mrr` (Dashboard-aligned).
   * All monetary totals are converted to CAD using `ratesToCad` (fetched via getExchangeRatesToCad).
   */
  static async getStripeBillingMetrics(
    ratesToCad?: ExchangeRatesToCad,
  ): Promise<StripeBillingMetrics> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const rates = ratesToCad ?? (await getExchangeRatesToCad());

    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const monthStartIso = monthStart.toISOString();

    const billingStatuses = ['active', 'trialing', 'past_due'];

    const [
      subsRes,
      paidByPaidAtRes,
      paidNoPaidAtRes,
      paidAllTimeRes,
      openInvoicesRes,
      paidCountRes,
      openCountRes,
      totalCountRes,
      stripeMrrRes,
    ] = await Promise.all([
      supabase
        .from('stripe_subscriptions')
        .select('stripe_subscription_id, status, kind, plan_label, amount_monthly, items, business_id, currency')
        .in('status', billingStatuses),
      supabase
        .from('stripe_invoices')
        .select('amount_paid, currency')
        .eq('status', 'paid')
        .gte('paid_at', monthStartIso),
      supabase
        .from('stripe_invoices')
        .select('amount_paid, currency')
        .eq('status', 'paid')
        .is('paid_at', null)
        .gte('invoice_created_at', monthStartIso),
      supabase.from('stripe_invoices').select('amount_paid, currency').eq('status', 'paid'),
      supabase.from('stripe_invoices').select('amount_due, currency').eq('status', 'open'),
      supabase.from('stripe_invoices').select('*', { count: 'exact', head: true }).eq('status', 'paid'),
      supabase.from('stripe_invoices').select('*', { count: 'exact', head: true }).eq('status', 'open'),
      supabase.from('stripe_invoices').select('*', { count: 'exact', head: true }),
      supabase.functions.invoke('get-stripe-mrr', { body: { currency: 'cad' } }),
    ]);

    if (subsRes.error) throw subsRes.error;
    if (paidByPaidAtRes.error) throw paidByPaidAtRes.error;
    if (paidNoPaidAtRes.error) throw paidNoPaidAtRes.error;
    if (paidAllTimeRes.error) throw paidAllTimeRes.error;
    if (openInvoicesRes.error) throw openInvoicesRes.error;

    const seenSubIds = new Set<string>();
    let mrr_total = 0;
    let mrr_platform = 0;
    let mrr_hosting = 0;
    let mrr_other = 0;
    let mrr_external = 0;
    let external_subscriptions = 0;
    let active_subscriptions = 0;
    let trialing_subscriptions = 0;
    let past_due_subscriptions = 0;

    // Sales-by-item: group strictly by Stripe product id (ignore Nexus plan names).
    const itemBreakdown = new Map<string, StripeItemBreakdownRow>();
    const addToProductBreakdown = (
      groupKey: string,
      label: string,
      productId: string | null,
      monthlyCad: number,
    ) => {
      const row = itemBreakdown.get(groupKey) || {
        label,
        product_id: productId,
        count: 0,
        monthly_total: 0,
      };
      // Prefer a real Stripe product name if a later item has one.
      if (productId && label && !label.startsWith('Unlabeled') && row.label.startsWith('Unlabeled')) {
        row.label = label;
      }
      row.count++;
      row.monthly_total += monthlyCad;
      itemBreakdown.set(groupKey, row);
    };

    for (const sub of subsRes.data || []) {
      const subId = sub.stripe_subscription_id as string;
      if (!subId || seenSubIds.has(subId)) continue;
      seenSubIds.add(subId);

      const status = (sub.status as string || '').toLowerCase();
      const amount = convertDollarsToCad(
        resolveMirrorSubscriptionMonthlyAmount(
          sub.amount_monthly as number | null,
          sub.items,
        ),
        (sub as { currency?: string | null }).currency,
        rates,
      );
      const kind = (sub.kind as string || 'other').toLowerCase();
      const hasBusiness = !!(sub as { business_id?: string | null }).business_id;

      mrr_total += amount;
      if (!hasBusiness) {
        mrr_external += amount;
        external_subscriptions++;
      } else if (kind === 'platform') {
        mrr_platform += amount;
      } else if (kind === 'hosting') {
        mrr_hosting += amount;
      } else {
        mrr_other += amount;
      }

      if (status === 'active') active_subscriptions++;
      else if (status === 'trialing') trialing_subscriptions++;
      else if (status === 'past_due') past_due_subscriptions++;

      // Sales-by-item matches Stripe MRR rules: exclude trials.
      if (status === 'trialing') continue;

      const currency = (sub as { currency?: string | null }).currency;
      const entries = itemEntriesFromMirrorItems(sub.items);
      if (entries.length === 0) {
        addToProductBreakdown(
          `sub:${subId}`,
          'Other',
          null,
          amount,
        );
      } else {
        for (const entry of entries) {
          const productId = entry.productId;
          const groupKey = productId
            ? `product:${productId}`
            : entry.priceId
              ? `price:${entry.priceId}`
              : `unknown:${entry.productName || entry.nickname || 'other'}`;
          const label = productId
            ? entry.productName || `Unlabeled (${productId})`
            : entry.priceId
              ? `Unlabeled (${entry.priceId})`
              : 'Other';
          addToProductBreakdown(
            groupKey,
            label,
            productId,
            convertDollarsToCad(entry.monthly, currency, rates),
          );
        }
      }
    }

    const by_item = Array.from(itemBreakdown.values()).sort(
      (a, b) => b.monthly_total - a.monthly_total,
    );

    const collected_this_month =
      (paidByPaidAtRes.data || []).reduce(
        (sum, inv) =>
          sum +
          convertCentsToCadCents(
            Number(inv.amount_paid) || 0,
            (inv as { currency?: string | null }).currency,
            rates,
          ),
        0,
      ) +
      (paidNoPaidAtRes.data || []).reduce(
        (sum, inv) =>
          sum +
          convertCentsToCadCents(
            Number(inv.amount_paid) || 0,
            (inv as { currency?: string | null }).currency,
            rates,
          ),
        0,
      );
    const collected_all_time = (paidAllTimeRes.data || []).reduce(
      (sum, inv) =>
        sum +
        convertCentsToCadCents(
          Number(inv.amount_paid) || 0,
          (inv as { currency?: string | null }).currency,
          rates,
        ),
      0,
    );
    const outstanding = (openInvoicesRes.data || []).reduce(
      (sum, inv) =>
        sum +
        convertCentsToCadCents(
          Number(inv.amount_due) || 0,
          (inv as { currency?: string | null }).currency,
          rates,
        ),
      0,
    );

    // AI token purchases (PaymentIntents) — not in stripe_invoices mirror.
    const { data: tokenPurchases, error: tokenErr } = await supabase
      .from('ai_token_transactions')
      .select('id, tokens, metadata, created_at, operation_type')
      .eq('transaction_type', 'purchase');

    if (tokenErr) {
      console.warn('AI token purchase metrics unavailable:', tokenErr.message);
    }

    let ai_tokens_this_month = 0;
    let ai_tokens_all_time = 0;
    let ai_token_purchases_this_month = 0;
    let ai_tokens_units_this_month = 0;
    const monthStartMs = monthStart.getTime();

    for (const row of tokenPurchases || []) {
      const usdCents = aiTokenPurchaseAmountCentsUsd(row.metadata);
      if (usdCents <= 0) continue;
      const cadCents = convertCentsToCadCents(usdCents, 'usd', rates);
      ai_tokens_all_time += cadCents;
      const created = new Date(row.created_at as string).getTime();
      if (created >= monthStartMs) {
        ai_tokens_this_month += cadCents;
        ai_token_purchases_this_month += 1;
        ai_tokens_units_this_month += Math.max(0, Number(row.tokens) || 0);
      }
    }

    // Prefer Stripe Dashboard-aligned MRR when get-stripe-mrr succeeds.
    let mrr_from_stripe = false;
    const stripeMrrData = (stripeMrrRes.data ?? null) as {
      ok?: boolean;
      mrr_cents?: number;
      currency?: string;
      error?: string;
      source?: string;
    } | null;
    // functions.invoke may set `error` on non-2xx even when a JSON body is present.
    if (stripeMrrData?.ok && typeof stripeMrrData.mrr_cents === 'number') {
      const mrrCadCents = convertCentsToCadCents(
        stripeMrrData.mrr_cents,
        stripeMrrData.currency || 'cad',
        rates,
      );
      mrr_total = mrrCadCents / 100;
      mrr_from_stripe = true;
    } else {
      const reason =
        stripeMrrRes.error?.message ||
        stripeMrrData?.error ||
        'unknown error';
      console.warn('Stripe MRR unavailable, using mirror total:', reason);
    }

    return {
      mrr_total,
      mrr_from_stripe,
      mrr_platform,
      mrr_hosting,
      mrr_other,
      mrr_external,
      external_subscriptions,
      active_subscriptions,
      trialing_subscriptions,
      past_due_subscriptions,
      collected_this_month,
      collected_all_time,
      outstanding,
      open_invoices: openCountRes.count ?? 0,
      paid_invoices: paidCountRes.count ?? 0,
      total_invoices: totalCountRes.count ?? 0,
      by_item,
      ai_tokens_this_month,
      ai_tokens_all_time,
      ai_token_purchases_this_month,
      ai_tokens_units_this_month,
      display_currency: 'CAD',
    };
  }

  /**
   * Renewal anchors for the invoices-tab cash-flow chart.
   * Active and trialing only. Past due is left out — that money is already an open invoice.
   * The mirror rarely has current_period_end, so the day comes from the latest paid invoice.
   */
  static async listRecurringBillAnchors(): Promise<{
    anchors: RecurringBillAnchor[];
    unscheduled: number;
  }> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const [subsRes, invoicesRes] = await Promise.all([
      supabase
        .from('stripe_subscriptions')
        .select(
          'stripe_subscription_id, status, amount_monthly, currency, interval, current_period_end, cancel_at, trial_end',
        )
        .in('status', ['active', 'trialing']),
      supabase
        .from('stripe_invoices')
        .select('stripe_subscription_id, period_end, status')
        .in('status', ['paid', 'open'])
        .not('stripe_subscription_id', 'is', null)
        .not('period_end', 'is', null)
        .order('period_end', { ascending: false })
        .limit(4000),
    ]);

    if (subsRes.error) throw subsRes.error;
    if (invoicesRes.error) throw invoicesRes.error;

    const latestPaid = new Map<string, string>();
    const latestOpen = new Map<string, string>();
    for (const row of invoicesRes.data || []) {
      const id = row.stripe_subscription_id as string | null;
      const periodEnd = row.period_end as string | null;
      if (!id || !periodEnd) continue;
      if (row.status === 'paid') {
        if (!latestPaid.has(id)) latestPaid.set(id, periodEnd);
      } else if (!latestOpen.has(id)) {
        latestOpen.set(id, periodEnd);
      }
    }

    const anchors: RecurringBillAnchor[] = [];
    let unscheduled = 0;
    for (const row of subsRes.data || []) {
      const amountMonthly = Number(row.amount_monthly) || 0;
      if (amountMonthly <= 0) continue;
      const id = (row.stripe_subscription_id as string | null) || '';
      const status = (row.status as string | null) || '';
      const trialEnd = (row.trial_end as string | null) || null;
      let anchorIso: string | null = null;
      let notBeforeIso: string | null = null;
      if (status === 'trialing' && trialEnd) {
        anchorIso = trialEnd;
        notBeforeIso = trialEnd;
      } else if (row.current_period_end) {
        anchorIso = row.current_period_end as string;
      } else if (id && latestPaid.has(id)) {
        anchorIso = latestPaid.get(id) || null;
      } else if (id && latestOpen.has(id)) {
        anchorIso = latestOpen.get(id) || null;
      }
      if (!anchorIso) {
        unscheduled += 1;
        continue;
      }
      anchors.push({
        amountMonthly,
        currency: (row.currency as string | null) || 'usd',
        interval: (row.interval as string | null) || null,
        anchorIso,
        notBeforeIso,
        cancelAtIso: (row.cancel_at as string | null) || null,
      });
    }

    return { anchors, unscheduled };
  }

  /**
   * Recent paid AI token purchases for Super Admin billing.
   */
  static async listAiTokenPurchases(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<{ purchases: AiTokenPurchaseRow[]; total: number }> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 50;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const rates = await getExchangeRatesToCad();

    const { data, error, count } = await supabase
      .from('ai_token_transactions')
      .select('id, business_id, tokens, metadata, operation_type, created_at, businesses(name)', {
        count: 'exact',
      })
      .eq('transaction_type', 'purchase')
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;

    const purchases: AiTokenPurchaseRow[] = (data || [])
      .map((row: any) => {
        const usdCents = aiTokenPurchaseAmountCentsUsd(row.metadata);
        const meta = (row.metadata || {}) as Record<string, unknown>;
        return {
          id: row.id,
          business_id: row.business_id,
          business_name: row.businesses?.name ?? null,
          tokens: Number(row.tokens) || 0,
          amount_cents_usd: usdCents,
          amount_cad_cents: convertCentsToCadCents(usdCents, 'usd', rates),
          operation_type: row.operation_type || null,
          stripe_payment_intent_id:
            typeof meta.stripe_payment_intent_id === 'string'
              ? meta.stripe_payment_intent_id
              : null,
          created_at: row.created_at,
        };
      })
      // Free gifts / initial credits have no Stripe amount — exclude from billing list.
      .filter((row) => row.amount_cents_usd > 0);

    return { purchases, total: count ?? 0 };
  }

  /**
   * Stripe subscriptions + invoices for one business from mirror tables.
   */
  static async getBusinessStripeBilling(businessId: string): Promise<{
    subscriptions: StripeSubscriptionMirrorRow[];
    invoices: StripeInvoiceMirrorRow[];
  }> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data: business, error: bizErr } = await supabase
      .from('businesses')
      .select('stripe_customer_id')
      .eq('id', businessId)
      .single();

    if (bizErr) throw bizErr;

    const stripeCustomerId = (business?.stripe_customer_id as string | null)?.trim() || null;

    const subQuery = supabase
      .from('stripe_subscriptions')
      .select('*')
      .order('current_period_end', { ascending: false });

    const invQuery = supabase
      .from('stripe_invoices')
      .select('*')
      .order('invoice_created_at', { ascending: false })
      .limit(100);

    const [subsRes, invByBizRes, invByCustRes] = await Promise.all([
      subQuery.or(`business_id.eq.${businessId}${stripeCustomerId ? `,stripe_customer_id.eq.${stripeCustomerId}` : ''}`),
      invQuery.eq('business_id', businessId),
      stripeCustomerId
        ? supabase
            .from('stripe_invoices')
            .select('*')
            .eq('stripe_customer_id', stripeCustomerId)
            .order('invoice_created_at', { ascending: false })
            .limit(100)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (subsRes.error) throw subsRes.error;
    if (invByBizRes.error) throw invByBizRes.error;
    if (invByCustRes.error) throw invByCustRes.error;

    const invoiceMap = new Map<string, StripeInvoiceMirrorRow>();
    for (const inv of [...(invByBizRes.data || []), ...(invByCustRes.data || [])]) {
      invoiceMap.set(inv.stripe_invoice_id, inv as StripeInvoiceMirrorRow);
    }

    const subscriptions = ((subsRes.data || []) as StripeSubscriptionMirrorRow[])
      .filter((s) => !isCanceledStripeSubscriptionStatus(s.status));
    const activeStatuses = new Set(['active', 'trialing', 'past_due']);
    subscriptions.sort((a, b) => {
      const aActive = activeStatuses.has(a.status) ? 0 : 1;
      const bActive = activeStatuses.has(b.status) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (b.current_period_end || '').localeCompare(a.current_period_end || '');
    });

    return {
      subscriptions,
      invoices: Array.from(invoiceMap.values()).sort(
        (a, b) => b.invoice_created_at.localeCompare(a.invoice_created_at),
      ),
    };
  }

  static async listReferralPartners(): Promise<ReferralPartnerListItem[]> {
    if (!supabase) throw new Error('Supabase client not initialized');

    const { data: partners, error } = await supabase
      .from('referral_partners')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    const partnerIds = (partners || []).map((p) => p.id as string);
    if (partnerIds.length === 0) return [];

    const [{ data: attributions }, { data: commissions }] = await Promise.all([
      supabase.from('referral_attributions').select('partner_id').in('partner_id', partnerIds),
      supabase
        .from('referral_commissions')
        .select('partner_id, commission_amount_cents, status')
        .in('partner_id', partnerIds),
    ]);

    const signupCounts = new Map<string, number>();
    for (const row of attributions || []) {
      const pid = row.partner_id as string;
      signupCounts.set(pid, (signupCounts.get(pid) || 0) + 1);
    }

    const accrued = new Map<string, number>();
    const paid = new Map<string, number>();
    for (const row of commissions || []) {
      const pid = row.partner_id as string;
      const cents = Number(row.commission_amount_cents) || 0;
      if (row.status === 'accrued') accrued.set(pid, (accrued.get(pid) || 0) + cents);
      if (row.status === 'paid') paid.set(pid, (paid.get(pid) || 0) + cents);
    }

    return (partners || []).map((p) => ({
      ...(p as ReferralPartnerRow),
      signups: signupCounts.get(p.id as string) || 0,
      accrued_cents: accrued.get(p.id as string) || 0,
      paid_cents: paid.get(p.id as string) || 0,
    }));
  }

  static async syncReferralPartnerCoupon(partnerId: string): Promise<string | null> {
    if (!supabase) throw new Error('Supabase client not initialized');

    const { data, error } = await supabase.functions.invoke('stripe-subscription', {
      body: { action: 'sync-referral-partner-coupon', partnerId },
    });

    if (error) {
      let detail = error instanceof Error ? error.message : 'Stripe coupon sync failed';
      const context = (error as { context?: Response }).context;
      if (context) {
        try {
          const body = await context.json();
          if (body && typeof body === 'object' && 'error' in body && body.error) {
            detail = String(body.error);
          }
        } catch {
          /* ignore parse errors */
        }
      }
      if (detail.includes('non-2xx') || detail.includes('Invalid action')) {
        detail =
          'stripe-subscription edge function is not deployed with sync-referral-partner-coupon yet. Run: supabase functions deploy stripe-subscription';
      }
      throw new Error(detail);
    }
    if (data?.error) throw new Error(String(data.error));
    return (data?.couponId as string | null) ?? null;
  }

  static async createReferralPartner(
    params: CreateReferralPartnerParams,
  ): Promise<CreateReferralPartnerResult> {
    if (!supabase) throw new Error('Supabase client not initialized');

    const code = params.code.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '');
    if (!code) throw new Error('Partner code is required');

    const { data: existing } = await supabase
      .from('referral_partners')
      .select('id, name, code')
      .eq('code', code)
      .maybeSingle();

    if (existing) {
      throw new Error(
        `Partner code "${code}" is already used by ${existing.name}. Choose a different code or use the existing partner below.`,
      );
    }

    const discountEnabled = params.signup_discount_enabled === true;
    const discountPercent = discountEnabled ? params.signup_discount_percent ?? null : null;

    const { data, error } = await supabase
      .from('referral_partners')
      .insert({
        name: params.name.trim(),
        code,
        email: params.email?.trim() || null,
        commission_type: params.commission_type,
        commission_percent: params.commission_percent,
        signup_discount_enabled: discountEnabled,
        signup_discount_percent: discountPercent,
        notes: params.notes?.trim() || null,
        status: 'awaiting_approval',
      })
      .select('*')
      .single();

    if (error) {
      throw new Error(formatReferralPartnerDbError(error));
    }

    let partner = data as ReferralPartnerRow;
    let couponSyncWarning: string | undefined;

    if (discountEnabled && discountPercent) {
      try {
        await SuperAdminService.syncReferralPartnerCoupon(partner.id);
        const { data: refreshed } = await supabase
          .from('referral_partners')
          .select('*')
          .eq('id', partner.id)
          .single();
        if (refreshed) partner = refreshed as ReferralPartnerRow;
      } catch (syncErr) {
        couponSyncWarning =
          syncErr instanceof Error
            ? syncErr.message
            : 'Stripe signup discount could not be configured. Partner was created — retry discount sync after deploying stripe-subscription.';
      }
    }

    return { partner, couponSyncWarning };
  }

  static async updateReferralPartner(
    partnerId: string,
    updates: Partial<
      Pick<
        ReferralPartnerRow,
        | 'name'
        | 'email'
        | 'status'
        | 'notes'
        | 'signup_discount_enabled'
        | 'signup_discount_percent'
      >
    >,
  ): Promise<void> {
    if (!supabase) throw new Error('Supabase client not initialized');

    const { error } = await supabase.from('referral_partners').update(updates).eq('id', partnerId);
    if (error) throw error;

    if (
      updates.signup_discount_enabled !== undefined ||
      updates.signup_discount_percent !== undefined
    ) {
      await SuperAdminService.syncReferralPartnerCoupon(partnerId);
    }
  }

  static async getReferralPartnerDetail(partnerId: string): Promise<{
    partner: ReferralPartnerRow;
    attributions: ReferralAttributionRow[];
    commissions: ReferralCommissionRow[];
  }> {
    if (!supabase) throw new Error('Supabase client not initialized');

    const [partnerRes, attrRes, commRes] = await Promise.all([
      supabase.from('referral_partners').select('*').eq('id', partnerId).single(),
      supabase
        .from('referral_attributions')
        .select('*, businesses(name, email)')
        .eq('partner_id', partnerId)
        .order('attributed_at', { ascending: false }),
      supabase
        .from('referral_commissions')
        .select('*, businesses(name)')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false })
        .limit(200),
    ]);

    if (partnerRes.error) throw partnerRes.error;
    if (attrRes.error) throw attrRes.error;
    if (commRes.error) throw commRes.error;

    return {
      partner: partnerRes.data as ReferralPartnerRow,
      attributions: (attrRes.data || []) as ReferralAttributionRow[],
      commissions: (commRes.data || []) as ReferralCommissionRow[],
    };
  }

  static async markReferralPartnerPayout(partnerId: string, notes?: string): Promise<{ payoutCount: number }> {
    if (!supabase) throw new Error('Supabase client not initialized');

    const { data: accrued, error: accErr } = await supabase
      .from('referral_commissions')
      .select('id, commission_amount_cents, currency')
      .eq('partner_id', partnerId)
      .eq('status', 'accrued');

    if (accErr) throw accErr;
    if (!accrued?.length) throw new Error('No accrued commissions to pay out');

    const byCurrency = new Map<string, { ids: string[]; total: number }>();
    for (const row of accrued) {
      const currency = (row.currency as string) || 'usd';
      const bucket = byCurrency.get(currency) || { ids: [], total: 0 };
      bucket.ids.push(row.id as string);
      bucket.total += Number(row.commission_amount_cents) || 0;
      byCurrency.set(currency, bucket);
    }

    let payoutCount = 0;
    for (const [currency, bucket] of byCurrency) {
      const { data: payout, error: payoutErr } = await supabase
        .from('referral_partner_payouts')
        .insert({
          partner_id: partnerId,
          amount_cents: bucket.total,
          currency,
          notes: notes?.trim() || null,
        })
        .select('id')
        .single();

      if (payoutErr) throw payoutErr;

      const { error: updErr } = await supabase
        .from('referral_commissions')
        .update({ status: 'paid', payout_id: payout.id })
        .in('id', bucket.ids);

      if (updErr) throw updErr;
      payoutCount++;
    }

    return { payoutCount };
  }

  // ─────────────────────────────────────────────────────────
  // Signup funnel analytics (signup_funnel_events, super-admin RLS read)
  // ─────────────────────────────────────────────────────────

  /**
   * Load raw signup funnel events for a window and aggregate them client-side
   * into the shape the Signups tab renders. Volumes are modest (pre-signup
   * traffic only), so paging the raw stream keeps this simple and flexible.
   */
  /** Page the raw event stream for a window (RLS limits reads to super admins). */
  private static async loadSignupFunnelEvents(days: number): Promise<SignupFunnelEventRow[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);
    since.setHours(0, 0, 0, 0);

    const events: SignupFunnelEventRow[] = [];
    const PAGE = 1000;
    for (let page = 0; page < 30; page++) {
      const { data, error } = await supabase
        .from('signup_funnel_events')
        .select(
          'session_id, visitor_id, flow, event_type, step, event_data, referral_code, utm, referrer, page_url, created_at',
        )
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: true })
        .range(page * PAGE, page * PAGE + PAGE - 1);
      if (error) throw error;
      events.push(...((data || []) as SignupFunnelEventRow[]));
      if (!data || data.length < PAGE) break;
    }
    return events;
  }

  /**
   * KPI-style conversion funnels — one report per signup entry path
   * (standard, extended, partner, Signup-nexus).
   * Matches Online Booking Volume: hero % + step counts as horizontal bars.
   */
  static async getSignupFlowConversionReports(days: number): Promise<SignupFlowConversionReport[]> {
    const events = await SuperAdminService.loadSignupFunnelEvents(days);

    type Marker = { type: string; step?: string | RegExp };
    const matches = (e: SignupFunnelEventRow, m: Marker): boolean => {
      if (e.event_type !== m.type) return false;
      if (m.step === undefined) return true;
      if (typeof m.step === 'string') return e.step === m.step;
      return !!e.step && m.step.test(e.step);
    };

    const STANDARD_STEPS: { label: string; markers: Marker[] }[] = [
      { label: 'Landing', markers: [{ type: 'page_view', step: 'landing' }, { type: 'step_view', step: 'account' }, { type: 'click', step: 'landing' }] },
      { label: 'Account', markers: [{ type: 'step_complete', step: 'account' }] },
      { label: 'Verified', markers: [{ type: 'step_complete', step: 'verify' }] },
      { label: 'Business', markers: [{ type: 'step_view', step: 'business' }] },
      { label: 'Created', markers: [{ type: 'signup_completed' }] },
    ];
    // Signup-nexus: landing (email capture) → password → shared verify/business.
    const NEXUS_STEPS: { label: string; markers: Marker[] }[] = [
      {
        label: 'Landing',
        markers: [
          { type: 'page_view', step: 'landing' },
          { type: 'step_view', step: 'cta' },
          { type: 'click', step: 'landing' },
        ],
      },
      {
        label: 'Email',
        markers: [
          { type: 'step_complete', step: 'email' },
          // Reached password with email already captured (hero submit).
          { type: 'step_view', step: 'password' },
        ],
      },
      {
        label: 'Password',
        // step_complete 'account' fires after password when OTP auto-sends.
        markers: [
          { type: 'step_complete', step: 'password' },
          { type: 'step_complete', step: 'account' },
        ],
      },
      { label: 'Verified', markers: [{ type: 'step_complete', step: 'verify' }] },
      { label: 'Business', markers: [{ type: 'step_view', step: 'business' }] },
      { label: 'Created', markers: [{ type: 'signup_completed' }] },
    ];
    const EXTENDED_STEPS: { label: string; markers: Marker[] }[] = [
      { label: 'Landing', markers: [{ type: 'page_view', step: 'landing' }] },
      { label: 'Questions', markers: [{ type: 'step_complete', step: /^q_/ }] },
      { label: 'Email', markers: [{ type: 'step_complete', step: 'email' }] },
      { label: 'Verified', markers: [{ type: 'step_complete', step: 'verify' }] },
      { label: 'Password', markers: [{ type: 'step_complete', step: 'password' }] },
      { label: 'Business', markers: [{ type: 'step_complete', step: 'business_info' }] },
      { label: 'Plan', markers: [{ type: 'step_complete', step: 'plan_preference' }] },
      { label: 'Created', markers: [{ type: 'signup_completed' }] },
    ];

    type FlowKey = SignupFlowConversionReport['key'];
    interface Acc {
      key: FlowKey;
      completed: boolean;
      /** Persistent visitor id (falls back to session id when never captured). */
      visitor: string;
      events: SignupFunnelEventRow[];
    }
    const sessions = new Map<string, Acc>();

    for (const e of events) {
      let acc = sessions.get(e.session_id);
      if (!acc) {
        acc = { key: 'standard', completed: false, visitor: `sess:${e.session_id}`, events: [] };
        sessions.set(e.session_id, acc);
      }
      acc.events.push(e);
      if (acc.visitor.startsWith('sess:') && e.visitor_id) acc.visitor = e.visitor_id;
      if (e.event_type === 'signup_completed') acc.completed = true;

      const partnerFlag = !!(e.event_data as { partner_flow?: boolean } | null)?.partner_flow;
      // Explicit flow tags win; also recover nexus traffic mis-tagged as standard
      // (e.g. before record_signup_event allowed 'nexus') via page_url.
      if (e.flow === 'nexus' || isSignupNexusPageUrl(e.page_url)) {
        acc.key = 'nexus';
      } else if (acc.key !== 'nexus' && (e.flow === 'partner' || e.referral_code || partnerFlag)) {
        acc.key = 'partner';
      } else if (acc.key === 'standard' && e.flow === 'extended') {
        acc.key = 'extended';
      }
    }

    const buildReport = (
      key: FlowKey,
      title: string,
      url: string,
      steps: { label: string; markers: Marker[] }[],
      sessionList: Acc[],
    ): SignupFlowConversionReport => {
      // Sessions that reached each stage (any event of that stage OR a later one).
      const reachedStage = (s: Acc, i: number): boolean => {
        for (let j = i; j < steps.length; j++) {
          if (s.events.some((e) => steps[j].markers.some((m) => matches(e, m)))) return true;
        }
        return false;
      };

      const buildMode = (
        countOf: (list: Acc[]) => number,
      ): SignupFlowConversionMode => {
        const stageCounts = steps.map((_, i) => countOf(sessionList.filter((s) => reachedStage(s, i))));
        // Completed sessions always count on the final stage even if the
        // signup_completed event was the only marker captured.
        const completed = countOf(sessionList.filter((s) => s.completed));
        if (stageCounts.length > 0) {
          stageCounts[stageCounts.length - 1] = Math.max(stageCounts[stageCounts.length - 1], completed);
        }
        const starts = stageCounts[0] || 0;
        const conversionRate = starts > 0 ? ((completed / starts) * 100).toFixed(1) : '0.0';
        return {
          value: `${conversionRate}%`,
          label: `Conversion Rate (${completed}/${starts})`,
          funnelData: steps.map((step, i) => ({ step: step.label, count: stageCounts[i] })),
        };
      };

      return {
        key,
        title,
        url,
        all: buildMode((list) => list.length),
        unique: buildMode((list) => new Set(list.map((s) => s.visitor)).size),
      };
    };

    const all = [...sessions.values()];
    return [
      buildReport(
        'standard',
        'Standard signup',
        '/signup',
        STANDARD_STEPS,
        all.filter((s) => s.key === 'standard'),
      ),
      buildReport(
        'nexus',
        'Signup-nexus',
        '/signup-nexus',
        NEXUS_STEPS,
        all.filter((s) => s.key === 'nexus'),
      ),
      buildReport(
        'extended',
        'Extended signup',
        '/sign-up-new-business',
        EXTENDED_STEPS,
        all.filter((s) => s.key === 'extended'),
      ),
      buildReport(
        'partner',
        'Partner signup',
        '/signup?ref={partner_code}',
        EXTENDED_STEPS,
        all.filter((s) => s.key === 'partner'),
      ),
    ];
  }

  /**
   * Emails captured at the signup email step across all flows.
   * Deduped by email (keeps the earliest capture); marks if any session completed.
   */
  static async getSignupCollectedEmails(days: number): Promise<SignupCollectedEmail[]> {
    const events = await SuperAdminService.loadSignupFunnelEvents(days);

    const completedSessions = new Set<string>();
    const completedEmails = new Set<string>();
    for (const e of events) {
      if (e.event_type !== 'signup_completed') continue;
      completedSessions.add(e.session_id);
      const data = (e.event_data ?? {}) as Record<string, unknown>;
      if (typeof data.email === 'string' && data.email.includes('@')) {
        completedEmails.add(data.email.trim().toLowerCase());
      }
    }

    const byEmail = new Map<string, SignupCollectedEmail>();

    for (const e of events) {
      const isEmailStep =
        e.event_type === 'step_complete' && e.step === 'email';
      if (!isEmailStep) continue;

      const data = (e.event_data ?? {}) as Record<string, unknown>;
      const raw = typeof data.email === 'string' ? data.email.trim().toLowerCase() : '';
      if (!raw || !raw.includes('@')) continue;

      let flow = e.flow || 'standard';
      if (e.flow === 'nexus' || isSignupNexusPageUrl(e.page_url)) flow = 'nexus';

      const captureSource =
        typeof data.source === 'string' && data.source.trim()
          ? data.source.trim()
          : null;

      const completed =
        completedSessions.has(e.session_id) || completedEmails.has(raw);

      const existing = byEmail.get(raw);
      if (!existing) {
        byEmail.set(raw, {
          email: raw,
          flow,
          captureSource,
          collectedAt: e.created_at,
          sessionId: e.session_id,
          completedSignup: completed,
          referralCode: e.referral_code,
        });
      } else {
        // Keep earliest capture; upgrade completed / metadata from later events.
        if (completed) existing.completedSignup = true;
        if (!existing.referralCode && e.referral_code) existing.referralCode = e.referral_code;
        if (!existing.captureSource && captureSource) existing.captureSource = captureSource;
      }
    }

    // If an email completed signup but we only saw it on signup_completed, still list it.
    for (const email of completedEmails) {
      if (byEmail.has(email)) continue;
      const completeEvent = events.find((e) => {
        if (e.event_type !== 'signup_completed') return false;
        const data = (e.event_data ?? {}) as Record<string, unknown>;
        return typeof data.email === 'string' && data.email.trim().toLowerCase() === email;
      });
      if (!completeEvent) continue;
      let flow = completeEvent.flow || 'standard';
      if (completeEvent.flow === 'nexus' || isSignupNexusPageUrl(completeEvent.page_url)) {
        flow = 'nexus';
      }
      byEmail.set(email, {
        email,
        flow,
        captureSource: null,
        collectedAt: completeEvent.created_at,
        sessionId: completeEvent.session_id,
        completedSignup: true,
        referralCode: completeEvent.referral_code,
      });
    }

    return [...byEmail.values()].sort(
      (a, b) => new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime(),
    );
  }

  static async getSignupFunnelAnalytics(days: number): Promise<SignupFunnelAnalytics> {
    const since = new Date();
    since.setDate(since.getDate() - days);
    since.setHours(0, 0, 0, 0);

    const events = await SuperAdminService.loadSignupFunnelEvents(days);

    // ── Per-session rollup ──
    interface SessionInfo {
      firstSeen: string;
      visitor: string | null;
      flow: string;
      stages: Set<string>;
      source: string;
      completed: boolean;
    }
    const sessions = new Map<string, SessionInfo>();
    const stageOf = (e: SignupFunnelEventRow): string | null => {
      if (e.event_type === 'page_view' && e.step === 'landing') return 'landing';
      if (e.event_type === 'step_view' && e.step === 'account') return 'account';
      if (e.event_type === 'step_complete' && e.step === 'account') return 'otp_sent';
      if (e.event_type === 'step_complete' && e.step === 'verify') return 'verified';
      if (e.event_type === 'step_view' && e.step === 'business') return 'business';
      if (e.event_type === 'signup_completed') return 'completed';
      return null;
    };
    const sourceOf = (e: SignupFunnelEventRow): string => {
      if (e.referral_code) return `partner:${e.referral_code}`;
      const utmSource = (e.utm as { utm_source?: string } | null)?.utm_source;
      if (utmSource) return `utm:${utmSource}`;
      if (e.referrer) {
        try {
          return new URL(e.referrer).hostname.replace(/^www\./, '');
        } catch {
          return 'referrer';
        }
      }
      return 'direct';
    };

    const errorCounts = new Map<string, number>();
    for (const e of events) {
      let info = sessions.get(e.session_id);
      if (!info) {
        info = {
          firstSeen: e.created_at,
          visitor: e.visitor_id,
          flow: e.flow || 'standard',
          stages: new Set(),
          source: sourceOf(e),
          completed: false,
        };
        sessions.set(e.session_id, info);
      }
      // First non-direct source wins (a later event may carry the referral code).
      if (info.source === 'direct') {
        const s = sourceOf(e);
        if (s !== 'direct') info.source = s;
      }
      // Explicit flow tags win; recover nexus via page_url when the DB coerced
      // the flow to 'standard' (RPC versions that predate the nexus flow).
      if (e.flow === 'nexus' || isSignupNexusPageUrl(e.page_url)) info.flow = 'nexus';
      else if (e.flow === 'extended' && info.flow !== 'nexus') info.flow = 'extended';
      const stage = stageOf(e);
      if (stage) info.stages.add(stage);
      if (e.event_type === 'signup_completed') info.completed = true;
      if (e.event_type === 'error') {
        const reason = ((e.event_data as { reason?: string } | null)?.reason || 'unknown').toString();
        errorCounts.set(reason, (errorCounts.get(reason) || 0) + 1);
      }
    }

    const all = [...sessions.values()];
    const totalSessions = all.length;
    const completedCount = all.filter((s) => s.completed).length;

    // ── Funnel (unique sessions that reached each stage) ──
    const FUNNEL_STAGES: { key: string; label: string }[] = [
      { key: 'landing', label: 'Visited signup' },
      { key: 'otp_sent', label: 'Submitted account details' },
      { key: 'verified', label: 'Verified email' },
      { key: 'business', label: 'Reached business info' },
      { key: 'completed', label: 'Account created' },
    ];
    // A session "reached" stage i if it logged that stage OR any later one
    // (later stages imply earlier ones even if an early event was missed).
    const stageCounts = FUNNEL_STAGES.map((_, i) =>
      all.filter((s) => FUNNEL_STAGES.slice(i).some((f) => s.stages.has(f.key))).length,
    );
    const funnelTop = stageCounts[0] || 0;
    const funnel = FUNNEL_STAGES.map((stage, i) => ({
      step: stage.key,
      label: stage.label,
      sessions: stageCounts[i],
      pctOfTop: funnelTop > 0 ? Math.round((stageCounts[i] / funnelTop) * 100) : 0,
      dropFromPrev: i === 0 ? 0 : Math.max(0, stageCounts[i - 1] - stageCounts[i]),
    }));

    // ── Daily series (sessions started + completions per day) ──
    const dayKey = (iso: string) => iso.slice(0, 10);
    const daily = new Map<string, { sessions: number; completed: number }>();
    for (let i = 0; i <= days; i++) {
      const d = new Date(since);
      d.setDate(d.getDate() + i);
      if (d > new Date()) break;
      daily.set(d.toISOString().slice(0, 10), { sessions: 0, completed: 0 });
    }
    for (const s of all) {
      const key = dayKey(s.firstSeen);
      const bucket = daily.get(key);
      if (bucket) {
        bucket.sessions++;
        if (s.completed) bucket.completed++;
      }
    }

    // ── Sources / flows ──
    const sourceMap = new Map<string, { sessions: number; completed: number }>();
    const flowMap = new Map<string, { sessions: number; completed: number }>();
    for (const s of all) {
      const src = sourceMap.get(s.source) || { sessions: 0, completed: 0 };
      src.sessions++;
      if (s.completed) src.completed++;
      sourceMap.set(s.source, src);

      const fl = flowMap.get(s.flow) || { sessions: 0, completed: 0 };
      fl.sessions++;
      if (s.completed) fl.completed++;
      flowMap.set(s.flow, fl);
    }

    return {
      totals: {
        sessions: totalSessions,
        visitors: new Set(all.map((s) => s.visitor).filter(Boolean)).size,
        completed: completedCount,
        conversionRate: totalSessions > 0 ? Math.round((completedCount / totalSessions) * 1000) / 10 : 0,
      },
      funnel,
      daily: [...daily.entries()].map(([date, v]) => ({ date, ...v })),
      sources: [...sourceMap.entries()]
        .map(([source, v]) => ({ source, ...v }))
        .sort((a, b) => b.sessions - a.sessions)
        .slice(0, 12),
      errors: [...errorCounts.entries()]
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count),
      flows: [...flowMap.entries()].map(([flow, v]) => ({ flow, ...v })),
    };
  }

  /**
   * Rolling per-visitor signup log: every session becomes one card showing how
   * far the visitor got (or that they completed signup). Newest first.
   */
  static async getSignupSessionLog(days: number, limit = 100): Promise<SignupSessionCard[]> {
    const events = await SuperAdminService.loadSignupFunnelEvents(days);

    // Journey definitions per flow. Each stage lists the (event_type, step)
    // markers that prove the visitor reached it; reaching a later stage
    // implies all earlier ones.
    type Marker = { type: string; step?: string | RegExp };
    const matches = (e: SignupFunnelEventRow, m: Marker): boolean => {
      if (e.event_type !== m.type) return false;
      if (m.step === undefined) return true;
      if (typeof m.step === 'string') return e.step === m.step;
      return !!e.step && m.step.test(e.step);
    };
    const JOURNEYS: Record<string, { label: string; markers: Marker[] }[]> = {
      standard: [
        { label: 'Visited signup', markers: [{ type: 'page_view', step: 'landing' }, { type: 'step_view', step: 'account' }] },
        { label: 'Entered account details', markers: [{ type: 'step_complete', step: 'account' }] },
        { label: 'Verified email', markers: [{ type: 'step_complete', step: 'verify' }] },
        { label: 'Business info', markers: [{ type: 'step_view', step: 'business' }] },
        { label: 'Signed up', markers: [{ type: 'signup_completed' }] },
      ],
      // Landing (email on page) → password → OTP verify → business → create.
      nexus: [
        {
          label: 'Visited landing',
          markers: [
            { type: 'page_view', step: 'landing' },
            { type: 'step_view', step: 'cta' },
            { type: 'click', step: 'landing' },
          ],
        },
        {
          label: 'Entered email',
          markers: [
            { type: 'step_complete', step: 'email' },
            { type: 'step_view', step: 'password' },
          ],
        },
        {
          label: 'Created password',
          markers: [
            { type: 'step_complete', step: 'password' },
            { type: 'step_complete', step: 'account' },
          ],
        },
        { label: 'Verified email', markers: [{ type: 'step_complete', step: 'verify' }] },
        { label: 'Business info', markers: [{ type: 'step_view', step: 'business' }] },
        { label: 'Signed up', markers: [{ type: 'signup_completed' }] },
      ],
      extended: [
        { label: 'Visited signup', markers: [{ type: 'page_view', step: 'landing' }] },
        { label: 'Answering questions', markers: [{ type: 'step_complete', step: /^q_/ }] },
        { label: 'Entered email', markers: [{ type: 'step_complete', step: 'email' }] },
        { label: 'Verified email', markers: [{ type: 'step_complete', step: 'verify' }] },
        { label: 'Created password', markers: [{ type: 'step_complete', step: 'password' }] },
        { label: 'Business info', markers: [{ type: 'step_complete', step: 'business_info' }] },
        { label: 'Choosing plan', markers: [{ type: 'step_complete', step: 'plan_preference' }] },
        { label: 'Signed up', markers: [{ type: 'signup_completed' }] },
      ],
    };

    const sourceOf = (e: SignupFunnelEventRow): string => {
      if (e.referral_code) return `partner:${e.referral_code}`;
      const utmSource = (e.utm as { utm_source?: string } | null)?.utm_source;
      if (utmSource) return `utm:${utmSource}`;
      if (e.referrer) {
        try {
          return new URL(e.referrer).hostname.replace(/^www\./, '');
        } catch {
          return 'referrer';
        }
      }
      return 'direct';
    };

    interface Acc {
      flow: string;
      startedAt: string;
      lastSeenAt: string;
      source: string;
      referralCode: string | null;
      email: string | null;
      businessId: string | null;
      completed: boolean;
      stageIndex: number;
      errors: string[];
      planSelected: string | null;
      events: SignupFunnelEventRow[];
    }
    const sessions = new Map<string, Acc>();

    for (const e of events) {
      let acc = sessions.get(e.session_id);
      if (!acc) {
        acc = {
          flow: e.flow || 'standard',
          startedAt: e.created_at,
          lastSeenAt: e.created_at,
          source: sourceOf(e),
          referralCode: e.referral_code,
          email: null,
          businessId: null,
          completed: false,
          stageIndex: 0,
          errors: [],
          planSelected: null,
          events: [],
        };
        sessions.set(e.session_id, acc);
      }
      acc.lastSeenAt = e.created_at;
      // Explicit flow tags win; recover nexus via page_url when flow was coerced to standard.
      if (e.flow === 'nexus' || isSignupNexusPageUrl(e.page_url)) acc.flow = 'nexus';
      else if (e.flow === 'extended' && acc.flow !== 'nexus') acc.flow = 'extended';
      if (acc.source === 'direct') {
        const s = sourceOf(e);
        if (s !== 'direct') acc.source = s;
      }
      if (!acc.referralCode && e.referral_code) acc.referralCode = e.referral_code;

      const data = (e.event_data ?? {}) as Record<string, unknown>;
      if (!acc.email && typeof data.email === 'string' && data.email.includes('@')) {
        acc.email = data.email;
      }
      if (e.event_type === 'signup_completed') {
        acc.completed = true;
        if (typeof data.business_id === 'string') acc.businessId = data.business_id;
      }
      if (e.event_type === 'error') {
        const reason = (typeof data.reason === 'string' ? data.reason : 'unknown').replace(/_/g, ' ');
        if (!acc.errors.includes(reason)) acc.errors.push(reason);
      }
      if (e.step === 'plan_selected' && typeof data.plan_id === 'string') {
        acc.planSelected = data.plan_id;
      }
      acc.events.push(e);
    }

    // Resolve furthest stage per session against its flow's journey.
    for (const acc of sessions.values()) {
      const journey = JOURNEYS[acc.flow] || JOURNEYS.standard;
      let furthest = 0;
      for (let i = journey.length - 1; i >= 0; i--) {
        if (acc.events.some((e) => journey[i].markers.some((m) => matches(e, m)))) {
          furthest = i;
          break;
        }
      }
      if (acc.completed) furthest = journey.length - 1;
      acc.stageIndex = furthest;
    }

    // Backfill identity for completed signups from the created business row.
    const businessIds = [...sessions.values()].map((a) => a.businessId).filter(Boolean) as string[];
    const businessById = new Map<string, { name: string | null; email: string | null }>();
    if (businessIds.length > 0) {
      const { data: bizRows } = await supabase
        .from('businesses')
        .select('id, name, email')
        .in('id', businessIds.slice(0, 200));
      for (const b of bizRows || []) {
        businessById.set(b.id as string, {
          name: (b.name as string | null) ?? null,
          email: (b.email as string | null) ?? null,
        });
      }
    }

    const ACTIVE_WINDOW_MS = 30 * 60 * 1000;
    const now = Date.now();

    return [...sessions.entries()]
      .map(([sessionId, acc]) => {
        const journey = JOURNEYS[acc.flow] || JOURNEYS.standard;
        const biz = acc.businessId ? businessById.get(acc.businessId) : undefined;
        const status: SignupSessionCard['status'] = acc.completed
          ? 'completed'
          : now - new Date(acc.lastSeenAt).getTime() < ACTIVE_WINDOW_MS
            ? 'active'
            : 'dropped';
        return {
          sessionId,
          flow: acc.flow,
          startedAt: acc.startedAt,
          lastSeenAt: acc.lastSeenAt,
          source: acc.source,
          referralCode: acc.referralCode,
          email: acc.email || biz?.email || null,
          businessId: acc.businessId,
          businessName: biz?.name ?? null,
          status,
          stages: journey.map((s) => s.label),
          stageIndex: acc.stageIndex,
          stageLabel: journey[acc.stageIndex]?.label ?? 'Visited signup',
          errors: acc.errors,
          planSelected: acc.planSelected,
        };
      })
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
      .slice(0, limit);
  }

  /**
   * Engagement buckets: how often each business is used, split trial vs paid.
   *
   * Engagement = any authenticated activity by ANY account tied to the
   * business (owner or team-member resources) — activity_logs carries the
   * business_id either way. Each business lands in exactly one bucket based on
   * its share of active days in the window (prorated by account age so a
   * 5-day-old trial used every day counts as daily, not weekly):
   *   daily ≥80% of days · every-3-days ≥1/3 · weekly ≥1/7 · monthly ≥1 day ·
   *   inactive = no activity at all.
   */
  static async getEngagementAnalytics(windowDays = 30): Promise<EngagementAnalytics> {
    const [{ data: activityRows, error: actErr }, { data: bizRows, error: bizErr }] =
      await Promise.all([
        supabase.rpc('get_business_activity_days', { p_days: windowDays }),
        supabase
          .from('businesses')
          .select('id, name, subscription_status, created_at')
          .in('subscription_status', ['trialing', 'active', 'past_due']),
      ]);
    if (actErr) throw actErr;
    if (bizErr) throw bizErr;

    const activeDaysByBiz = new Map<string, number>();
    for (const row of (activityRows || []) as { business_id: string; activity_date: string }[]) {
      activeDaysByBiz.set(row.business_id, (activeDaysByBiz.get(row.business_id) || 0) + 1);
    }

    const emptyGroup = (): EngagementGroup => ({
      buckets: { daily: [], every3: [], weekly: [], monthly: [], inactive: [] },
      total: 0,
    });
    const trial = emptyGroup();
    const paid = emptyGroup();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    for (const b of (bizRows || []) as {
      id: string;
      name: string | null;
      subscription_status: string;
      created_at: string;
    }[]) {
      const activeDays = activeDaysByBiz.get(b.id) || 0;
      const ageDays = Math.max(1, Math.ceil((now - new Date(b.created_at).getTime()) / DAY_MS));
      const effectiveWindow = Math.min(windowDays, ageDays);
      const rate = activeDays / effectiveWindow;

      let bucket: EngagementBucket;
      if (activeDays === 0) bucket = 'inactive';
      else if (rate >= 0.8) bucket = 'daily';
      else if (rate >= 1 / 3) bucket = 'every3';
      else if (rate >= 1 / 7) bucket = 'weekly';
      else bucket = 'monthly';

      const group = b.subscription_status === 'trialing' ? trial : paid;
      group.buckets[bucket].push({ businessId: b.id, name: b.name, activeDays });
      group.total++;
    }

    // Most-active first inside each bucket.
    for (const group of [trial, paid]) {
      for (const list of Object.values(group.buckets)) {
        list.sort((a, b) => b.activeDays - a.activeDays);
      }
    }

    return { windowDays, trial, paid };
  }

  /**
   * Trial funnel from the Stripe subscription mirror: active trials, how many
   * added a card, and historical trial → paid conversion. Platform plans only
   * (hosting and customer-service subscriptions are excluded).
   */
  static async getTrialAnalytics(): Promise<TrialAnalytics> {
    interface MirrorRow {
      business_id: string | null;
      status: string;
      plan_label: string | null;
      amount_monthly: number | null;
      trial_start: string | null;
      trial_end: string | null;
      has_default_payment_method: boolean | null;
      canceled_at: string | null;
    }

    const { data, error } = await supabase
      .from('stripe_subscriptions')
      .select(
        'business_id, status, plan_label, amount_monthly, trial_start, trial_end, has_default_payment_method, canceled_at',
      )
      .eq('kind', 'platform');
    if (error) throw error;

    const rows = (data || []) as MirrorRow[];
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;

    const trialing = rows.filter((r) => r.status === 'trialing');
    const hadTrial = rows.filter((r) => r.trial_end !== null);
    // needsSync: mirror rows exist but none carry trial fields yet (pre-upgrade
    // data) AND nothing is trialing per Stripe status.
    const needsSync =
      rows.length > 0 && hadTrial.length === 0 && trialing.length === 0 &&
      rows.every((r) => r.has_default_payment_method === null);

    const endedTrials = hadTrial.filter(
      (r) => r.trial_end !== null && new Date(r.trial_end).getTime() < now && r.status !== 'trialing',
    );
    // Converted = paid after the trial (active/past_due now, or canceled well
    // after the trial ended — i.e. they paid for a while then left).
    const GRACE_MS = DAY_MS;
    const converted = endedTrials.filter((r) => {
      if (r.status === 'active' || r.status === 'past_due' || r.status === 'unpaid') return true;
      if (r.status === 'canceled' && r.canceled_at && r.trial_end) {
        return new Date(r.canceled_at).getTime() > new Date(r.trial_end).getTime() + GRACE_MS;
      }
      return false;
    }).length;
    const lost = endedTrials.length - converted;

    // Identity for the active-trial list.
    const bizIds = trialing.map((r) => r.business_id).filter(Boolean) as string[];
    const bizById = new Map<string, { name: string | null; email: string | null }>();
    if (bizIds.length > 0) {
      const { data: bizRows } = await supabase
        .from('businesses')
        .select('id, name, email')
        .in('id', bizIds.slice(0, 200));
      for (const b of bizRows || []) {
        bizById.set(b.id as string, {
          name: (b.name as string | null) ?? null,
          email: (b.email as string | null) ?? null,
        });
      }
    }

    const activeTrialList = trialing
      .map((r) => {
        const biz = r.business_id ? bizById.get(r.business_id) : undefined;
        const endMs = r.trial_end ? new Date(r.trial_end).getTime() : null;
        return {
          businessId: r.business_id,
          businessName: biz?.name ?? null,
          email: biz?.email ?? null,
          planLabel: r.plan_label,
          amountMonthly: r.amount_monthly,
          trialStart: r.trial_start,
          trialEnd: r.trial_end,
          daysLeft: endMs !== null ? Math.max(0, Math.ceil((endMs - now) / DAY_MS)) : null,
          hasCard: r.has_default_payment_method === true,
        };
      })
      .sort((a, b) => {
        if (a.trialEnd === null) return 1;
        if (b.trialEnd === null) return -1;
        return new Date(a.trialEnd).getTime() - new Date(b.trialEnd).getTime();
      });

    return {
      totals: {
        activeTrials: trialing.length,
        activeWithCard: trialing.filter((r) => r.has_default_payment_method === true).length,
        converted,
        lost,
        conversionRate:
          converted + lost > 0 ? Math.round((converted / (converted + lost)) * 1000) / 10 : 0,
        totalTrials: hadTrial.length,
      },
      activeTrialList,
      needsSync,
    };
  }
} 